import { useState, useRef, useEffect, useMemo, useCallback, forwardRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  ArrowUp,
  List,
  Map as MapIcon,
  GripVertical,
  Pencil,
  MessageCircle,
  Trash2,
  Check,
  BadgeCheck,
  Compass,
  ChevronUp,
  BedDouble,
  Clock,
  MapPin,
  ExternalLink,
  Banknote,
  Quote,
  X,
  ChevronRight,
  RefreshCw,
  Sun,
  Moon,
  Menu as MenuIcon,
  Plus,
  Home as HomeIcon,
  Share2,
  Youtube,
  StickyNote,
} from "lucide-react";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  streamConcierge,
  ConciergeError,
  type ConciergeMessage,
} from "@/lib/concierge-stream";
import { useUsage, localDayParams } from "@/hooks/use-usage";
import UsageMeter from "@/components/UsageMeter";
import AccountMenu from "@/components/AccountMenu";
import { useAuth } from "@/context/AuthContext";
import {
  trackFirstMessageSent,
  trackMessageMilestone,
  trackReturningTripOpened,
  trackPlaceAddedFromInspiration,
  trackAffiliateClick,
  trackTripShared,
  trackTripBuilt,
  trackDecisionCardsShown,
  trackDecisionCardChosen,
} from "@/lib/analytics";
import { renderMarkdown } from "@/lib/markdown";
import { buildClickUrl, buildBookingSearchUrl } from "@/lib/clicks";
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
} from "@/lib/itinerary-display";
import { TripMap, buildMapsPlaceUrl } from "@/components/TripMap";
import TripEssentials from "@/components/TripEssentials";
import Inspiration from "@/components/Inspiration";
import DestinationHero from "@/components/DestinationHero";
import PlacePhoto from "@/components/PlacePhoto";
import { usePreferences } from "@/hooks/use-preferences";
import type { ExtractedPlace, InspirationVideo } from "@/lib/inspiration";
import {
  useJourneys,
  type Trip as JourneyTrip,
  type Day,
  type ItineraryItem,
  type Message,
  type MessageOptions,
  type TimeBucket,
  type ItemType,
} from "@/context/JourneyContext";
import { getCoverImage } from "@/lib/trip-helpers";

const GOOGLE_MAPS_API_KEY: string =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";

type Trip = JourneyTrip;

// ===== Constants =====
// Display constants live in src/lib/itinerary-display.ts so the TripMap
// component (used here and on the SharedTrip page) can render the same
// icons and labels.

// Authoritative valid values. If the AI emits something outside these,
// we normalize to a safe default to keep the UI from white-screening.
const VALID_ITEM_TYPES: ItemType[] = [
  "activity",
  "dining",
  "accommodation",
  "transport",
  "nightlife",
  "tip",
];

const VALID_BUCKETS: TimeBucket[] = ["morning", "afternoon", "evening", "night"];

const normalizeItemType = (t: unknown): ItemType =>
  typeof t === "string" && (VALID_ITEM_TYPES as string[]).includes(t)
    ? (t as ItemType)
    : "activity";

const normalizeBucket = (b: unknown): TimeBucket =>
  typeof b === "string" && (VALID_BUCKETS as string[]).includes(b)
    ? (b as TimeBucket)
    : "morning";

// ===== Mock data (used only for /journey/demo) =====
const MOCK_TRIP: Trip = {
  id: "demo",
  title: "Lisbon weekend",
  destination: "Lisbon, Portugal",
  startDate: "2026-05-15",
  endDate: "2026-05-17",
  coverImage:
    "https://images.unsplash.com/photo-1513735492246-483525079686?q=80&w=2070",
  createdAt: "2026-05-01T12:00:00Z",
  updatedAt: "2026-05-08T10:30:00Z",
  chatHistory: [
    {
      id: "m1",
      role: "assistant",
      content:
        "Welcome back. I've got a 3-day Lisbon weekend mapped out, focused on food and old-town walks. Anything to adjust before we get going?",
      timestamp: Date.now() - 1000 * 60 * 60 * 2,
    },
  ],
  days: [
    {
      id: "d1",
      date: "2026-05-15",
      title: "Arrival & first tastes",
      items: [
        {
          id: "d1-i1",
          timeBucket: "afternoon",
          specificTime: "2:30 PM",
          title: "Arrive at Humberto Delgado Airport",
          description:
            "Aerobus line 1 to Baixa-Chiado, around 25 minutes, €4.",
          type: "transport",
          coordinates: { lat: 38.7813, lng: -9.1359 },
          duration: 60,
        },
        {
          id: "d1-i2",
          timeBucket: "afternoon",
          title: "Walk Praça do Comércio at golden hour",
          description:
            "Lisbon's grand riverfront square. Best light around 5:30 PM in May, when the yellow trams catch the sun against the arch.",
          type: "activity",
          coordinates: { lat: 38.7075, lng: -9.1364 },
          address: "Praça do Comércio, 1100-148 Lisboa",
          duration: 60,
        },
        {
          id: "d1-i3",
          timeBucket: "evening",
          title: "Dinner at Cervejaria Ramiro",
          description:
            "Lisbon's seafood institution. Order the gambas à la Guilho and finish with a prego steak sandwich. Expect a 30-min wait.",
          type: "dining",
          coordinates: { lat: 38.7196, lng: -9.1356 },
          address: "Av. Almirante Reis 1, 1150-007 Lisboa",
          hours: "12:00 - 00:30, Tue-Sun",
          priceRange: "€€€",
          duration: 120,
        },
      ],
    },
    {
      id: "d2",
      date: "2026-05-16",
      title: "Alfama & azulejos",
      items: [
        {
          id: "d2-i1",
          timeBucket: "morning",
          title: "Coffee at Fábrica Coffee Roasters",
          description:
            "Specialty coffee on Rua das Portas de Santo Antão. Try the cortado.",
          type: "dining",
          coordinates: { lat: 38.7176, lng: -9.1383 },
          address: "R. das Portas de Santo Antão 136, 1150-269 Lisboa",
          hours: "08:00 - 19:00, daily",
          priceRange: "€",
          duration: 30,
        },
        {
          id: "d2-i2",
          timeBucket: "morning",
          title: "Tram 28 to Alfama",
          description:
            "Hop on at Praça Martim Moniz, get off near Miradouro de Santa Luzia. Pickpockets are a real problem on this line, keep your bag closed.",
          type: "transport",
          coordinates: { lat: 38.716, lng: -9.1361 },
          priceRange: "€3 single",
          duration: 25,
        },
        {
          id: "d2-i3",
          timeBucket: "morning",
          title: "Museu Nacional do Azulejo",
          description:
            "Lisbon's tile museum, set in a 16th-century convent. The Great Panorama of Lisbon is the highlight.",
          type: "activity",
          coordinates: { lat: 38.7245, lng: -9.1131 },
          address: "R. Madre de Deus 4, 1900-312 Lisboa",
          hours: "10:00 - 18:00, Tue-Sun",
          priceRange: "€8 admission",
          duration: 90,
        },
        {
          id: "d2-i4",
          timeBucket: "evening",
          title: "Dinner at Taberna da Rua das Flores",
          description:
            "No reservations, arrive before 7:30 PM or expect a wait. Daily menu read aloud by staff.",
          type: "dining",
          coordinates: { lat: 38.7104, lng: -9.143 },
          address: "R. das Flores 103, 1200-194 Lisboa",
          hours: "12:30 - 23:00, Mon-Sat",
          priceRange: "€€",
          duration: 120,
        },
      ],
    },
    {
      id: "d3",
      date: "2026-05-17",
      title: "Belém & departure",
      items: [
        {
          id: "d3-i1",
          timeBucket: "morning",
          title: "Pastéis de Belém for breakfast",
          description:
            "The original pastel de nata. Skip the line by ordering at the takeaway counter.",
          type: "dining",
          coordinates: { lat: 38.6974, lng: -9.203 },
          address: "R. de Belém 84-92, 1300-085 Lisboa",
          hours: "08:00 - 22:00, daily",
          priceRange: "€",
          duration: 30,
        },
        {
          id: "d3-i2",
          timeBucket: "morning",
          title: "Mosteiro dos Jerónimos",
          description:
            "UNESCO Manueline masterpiece. Free entry to the church, paid for the cloisters.",
          type: "activity",
          coordinates: { lat: 38.6979, lng: -9.2068 },
          address: "Praça do Império 1400-206 Lisboa",
          hours: "10:00 - 17:30, Tue-Sun",
          priceRange: "€12 cloisters",
          duration: 90,
        },
        {
          id: "d3-i3",
          timeBucket: "afternoon",
          specificTime: "1:00 PM",
          title: "Departure from LIS",
          description:
            "Aerobus from Marquês de Pombal, around 35 minutes back to the airport.",
          type: "transport",
          coordinates: { lat: 38.7813, lng: -9.1359 },
          duration: 60,
        },
      ],
    },
  ],
};

// ===== Helpers =====
const totalStops = (days: Day[]) =>
  days.reduce((sum, d) => sum + d.items.length, 0);

const formatDateRange = (
  startDate: string | null,
  endDate: string | null
): string => {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const startStr = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endStr = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${startStr} - ${endStr}`;
};

const formatDayDate = (date: string): string =>
  new Date(date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// Whole-day difference between two ISO dates. Returns null if either is
// missing, malformed, or yields a non-positive span. Used to render the
// "3 nights" badge on accommodation items with check-in/check-out set.
const computeNights = (
  checkIn?: string,
  checkOut?: string
): number | null => {
  if (!checkIn || !checkOut) return null;
  const a = new Date(`${checkIn}T00:00:00`);
  const b = new Date(`${checkOut}T00:00:00`);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  const diff = Math.round(
    (b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000)
  );
  return diff > 0 ? diff : null;
};

const formatShortDate = (date?: string): string | null => {
  if (!date) return null;
  const d = new Date(`${date}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const groupByBucket = (
  items: ItineraryItem[]
): Record<TimeBucket, ItineraryItem[]> => {
  const grouped: Record<TimeBucket, ItineraryItem[]> = {
    morning: [],
    afternoon: [],
    evening: [],
    night: [],
  };
  for (const item of items) {
    const bucket = normalizeBucket(item.timeBucket);
    grouped[bucket].push(item);
  }
  return grouped;
};

const newId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const sortDays = (days: Day[]): Day[] =>
  [...days].sort((a, b) => a.date.localeCompare(b.date));

// Resolve either a dayId or a date to a Day from the current state.
const findDay = (
  days: Day[],
  dayId?: string,
  date?: string
): Day | undefined => {
  if (dayId) {
    const byId = days.find((d) => d.id === dayId);
    if (byId) return byId;
  }
  if (date) {
    return days.find((d) => d.date === date);
  }
  return undefined;
};

const insertItem = (
  items: ItineraryItem[],
  newItem: ItineraryItem,
  insertBeforeItemId?: string,
  insertAfterItemId?: string
): ItineraryItem[] => {
  if (insertBeforeItemId) {
    const idx = items.findIndex((i) => i.id === insertBeforeItemId);
    if (idx !== -1) {
      return [...items.slice(0, idx), newItem, ...items.slice(idx)];
    }
  }
  if (insertAfterItemId) {
    const idx = items.findIndex((i) => i.id === insertAfterItemId);
    if (idx !== -1) {
      return [...items.slice(0, idx + 1), newItem, ...items.slice(idx + 1)];
    }
  }

  const targetRank = BUCKET_ORDER[newItem.timeBucket];
  let lastSameBucketIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].timeBucket === newItem.timeBucket) {
      lastSameBucketIdx = i;
      break;
    }
  }
  if (lastSameBucketIdx !== -1) {
    return [
      ...items.slice(0, lastSameBucketIdx + 1),
      newItem,
      ...items.slice(lastSameBucketIdx + 1),
    ];
  }

  const firstLaterIdx = items.findIndex(
    (i) => BUCKET_ORDER[i.timeBucket] > targetRank
  );
  if (firstLaterIdx !== -1) {
    return [
      ...items.slice(0, firstLaterIdx),
      newItem,
      ...items.slice(firstLaterIdx),
    ];
  }
  return [...items, newItem];
};

// ===== Geocoding via Maps JS SDK =====
// We use google.maps.Geocoder (loaded by @vis.gl/react-google-maps' APIProvider)
// rather than the REST endpoint, because Google's policy is that HTTP-referrer
// restricted API keys cannot call the Geocoding REST endpoint. The JS SDK
// honors referrer restrictions just like the Maps render.

let mapsReadyPromise: Promise<boolean> | null = null;

const waitForMaps = (timeoutMs = 10000): Promise<boolean> => {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.google?.maps?.Geocoder) return Promise.resolve(true);

  if (mapsReadyPromise) return mapsReadyPromise;

  mapsReadyPromise = new Promise((resolve) => {
    const start = Date.now();
    const interval = window.setInterval(() => {
      if (window.google?.maps?.Geocoder) {
        window.clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        window.clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });

  return mapsReadyPromise;
};

const geocodeAddress = async (
  query: string
): Promise<{ lat: number; lng: number } | null> => {
  if (!query.trim()) return null;
  const ready = await waitForMaps();
  if (!ready) {
    console.warn(
      "Maps SDK did not load in time, skipping geocode for:",
      query
    );
    return null;
  }
  try {
    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({ address: query });
    if (response?.results?.length > 0) {
      const loc = response.results[0].geometry.location;
      return { lat: loc.lat(), lng: loc.lng() };
    }
    return null;
  } catch (err: any) {
    // Geocoder rejects on ZERO_RESULTS, OVER_QUERY_LIMIT, REQUEST_DENIED, etc.
    // We log warnings (not errors) since some misses are normal for sketchy
    // free-text inputs like "Lunch at A Licorista".
    const status =
      err?.code ?? err?.message ?? (typeof err === "string" ? err : String(err));
    console.warn(`Geocoding "${query}" failed:`, status);
    return null;
  }
};

// Maps a client tool name to a human-readable status shown while the AI
// works. (Server tools like verify_place / web_search run server-side and
// don't reach the client, so structural client tools stand in for "the AI
// is assembling your trip".)
const toolStatusLabel = (toolName: string): string => {
  switch (toolName) {
    case "update_trip_metadata":
      return "Setting up your trip...";
    case "add_day":
    case "remove_day":
      return "Mapping out the days...";
    case "add_item":
    case "update_item":
    case "remove_item":
      return "Adding places...";
    case "add_day_note":
      return "Taking a note...";
    case "set_essentials":
      return "Gathering trip essentials...";
    case "present_options":
      return "Laying out your options...";
    default:
      return "Building your trip...";
  }
};

// ===== Chat subcomponents =====
const ThinkingDots = () => (
  <span className="inline-flex gap-1 opacity-50">
    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
    <span
      className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"
      style={{ animationDelay: "150ms" }}
    />
    <span
      className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"
      style={{ animationDelay: "300ms" }}
    />
  </span>
);

// Normalizes present_options tool input into a MessageOptions block.
// Model output is untrusted: enforce shapes, require non-empty label AND
// description, dedupe labels (label doubles as the React key and the
// choice identifier), clamp to 4 options and 4 pros/cons each. Returns
// null when fewer than 2 usable options survive; the server's
// tool_result validation mirrors this rule so the model learns when its
// call was rejected.
const normalizeOptionsInput = (input: any): MessageOptions | null => {
  const rawItems = Array.isArray(input?.options) ? input.options : [];
  const strings = (v: unknown) => {
    if (!Array.isArray(v)) return undefined;
    const out = v
      .filter((s): s is string => typeof s === "string" && !!s.trim())
      .slice(0, 4);
    // undefined, not [], so downstream length checks stay boolean-safe.
    return out.length ? out : undefined;
  };
  const seenLabels = new Set<string>();
  const items: MessageOptions["items"] = [];
  for (const o of rawItems) {
    if (
      !o ||
      typeof o.label !== "string" ||
      !o.label.trim() ||
      typeof o.description !== "string" ||
      !o.description.trim()
    ) {
      continue;
    }
    const label = o.label.trim();
    const labelKey = label.toLowerCase();
    if (seenLabels.has(labelKey)) continue;
    seenLabels.add(labelKey);
    items.push({
      label,
      description: o.description.trim(),
      pros: strings(o.pros),
      cons: strings(o.cons),
    });
    if (items.length === 4) break;
  }
  if (items.length < 2) return null;
  return {
    question: typeof input?.question === "string" ? input.question.trim() : "",
    items,
  };
};

// Chat messages are replayed to the API as { role, content } only, so a
// decision fork's substance must be serialized INTO the content: without
// this, the model's only record of the fork is its own one-line reply,
// and a typed answer ("the second one") or a next-day tap lands on a
// question the model can no longer see. Also guarantees non-empty content
// for a reply that consisted solely of the tool call (the Anthropic API
// rejects empty assistant messages).
const toApiMessage = (m: Message): ConciergeMessage => {
  let content = m.content;
  if (m.role === "assistant" && m.options) {
    const lines = m.options.items.map((opt) => {
      const chosen =
        m.options?.chosenLabel === opt.label ? " (user chose this)" : "";
      return `- ${opt.label}: ${opt.description}${chosen}`;
    });
    content =
      `${content}\n\n[Presented option cards${
        m.options.question ? `: ${m.options.question}` : ""
      }]\n${lines.join("\n")}`.trim();
  }
  // Trimmed, not just checked for empty. A reply cut off mid-stream can be
  // left holding nothing but the paragraph break the server injects
  // between agent turns, and the API rejects a message whose text is only
  // whitespace just as firmly as an empty one. Before this, one such
  // message sat in the history and failed every later send in that trip,
  // for good.
  const trimmed = (content || "").trim();
  return { role: m.role, content: trimmed || "(empty reply)" };
};

// Tappable decision cards under an assistant message. Cards go inert when
// the choice is made (chosen card stays highlighted as a record), while
// any stream is running, or once the conversation has moved past them
// (expired): a fork the user answered by typing must not stay tappable in
// scrollback, where a stray tap would send an answer to a question the
// model has moved beyond.
const DecisionCards = ({
  options,
  disabled,
  expired,
  onChoose,
}: {
  options: MessageOptions;
  disabled: boolean;
  expired: boolean;
  onChoose: (label: string) => void;
}) => {
  const answered = !!options.chosenLabel;
  const inert = disabled || answered || expired;
  return (
    <div className="mt-2.5">
      {options.question && (
        <div className="kicker text-ink-muted leading-[1.5] mb-2 normal-case tracking-[0.16em] uppercase">
          {options.question}
        </div>
      )}
      <div className="flex flex-col gap-2">
        {options.items.map((opt) => {
          const chosen = options.chosenLabel === opt.label;
          // Four states, per the README: chosen keeps the accent border and an
          // inset ring so it stays the record of the decision; answered
          // siblings and an expired set drop to 45%; a live set dims to 60%
          // while the concierge is working.
          const stateClass = chosen
            ? "border-accent shadow-[inset_0_0_0_1px_var(--fn-accent)]"
            : answered || expired
              ? "border-rule opacity-45"
              : disabled
                ? "border-rule opacity-60"
                : "border-rule hover:shadow-[0_12px_32px_-12px_rgba(var(--fn-shadow-rgb),0.25)] hover:-translate-y-0.5";
          return (
            <button
              key={opt.label}
              type="button"
              disabled={inert}
              onClick={() => onChoose(opt.label)}
              className={`text-left bg-card border px-3.5 py-3 flex flex-col gap-1.5 w-full transition-all duration-200 ${stateClass} ${inert ? "cursor-default" : ""}`}
            >
              <span className="flex items-center justify-between gap-2.5">
                <span className="font-serif text-base text-ink">{opt.label}</span>
                {chosen && (
                  <span className="relative h-[15px] w-[15px] border-[1.5px] border-accent shrink-0">
                    <svg viewBox="0 0 16 16" fill="none" className="absolute -top-[3px] -left-[2px] h-[19px] w-[19px]">
                      <path d="M2 9 C5 12, 6 13, 7 11 C9 6, 11 3, 15 1" stroke="var(--fn-accent)" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                  </span>
                )}
              </span>
              {opt.description && (
                <span className="text-[13px] leading-[1.5] text-ink-muted">{opt.description}</span>
              )}
              {!!(opt.pros?.length || opt.cons?.length) && (
                <span className="flex flex-col gap-0.5 text-xs leading-[1.45] text-ink-faint">
                  {opt.pros?.map((x, i) => <span key={`p-${i}`}>+ {x}</span>)}
                  {opt.cons?.map((x, i) => <span key={`c-${i}`}>− {x}</span>)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ChatMessage = ({
  message,
  isStreaming,
  statusLabel,
  showRetry,
  onRetry,
  chatBusy,
  optionsExpired,
  onChooseOption,
}: {
  message: Message;
  isStreaming: boolean;
  // When the assistant bubble is still empty and streaming, show what the
  // AI is doing ("Checking places...", "Building your trip...") instead of
  // dead air. Falls back to animated dots when there's no specific status.
  statusLabel?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  // Whether ANY stream is active (not just this message's): taps must not
  // fire while the assistant is mid-reply, since handleSend would drop them.
  chatBusy?: boolean;
  // True when the conversation has moved past this message (it is no
  // longer the latest); its cards render inert.
  optionsExpired?: boolean;
  onChooseOption?: (messageId: string, label: string) => void;
}) => {
  const isUser = message.role === "user";
  return (
    <div className="flex flex-col gap-1">
      <span className="kicker text-[9.5px] text-ink-faint mb-1.5">
        {isUser ? "You" : "Sunday Atlas"}
      </span>
      <div
        className={
          isUser
            ? "bg-paper-warm px-3 py-2.5 text-[13.5px] leading-[1.5] text-ink whitespace-pre-wrap"
            : "font-serif text-[14.5px] leading-[1.55] text-ink"
        }
      >
        {message.content
          ? isUser
            ? message.content
            : renderMarkdown(message.content)
          : isStreaming && !statusLabel
          ? // Empty bubble, no status yet: bare dots while we wait for the
            // first text or tool event.
            <ThinkingDots />
          : null}

        {/* Once text has started streaming the dots give way to a caret, and
            the status line sits directly under the partial reply inside the
            same block. Before the first token there is no caret, just dots. */}
        {!isUser && isStreaming && !!message.content && (
          <span className="inline-block w-0.5 h-[1em] align-[-0.15em] ml-0.5 bg-ink animate-caret-blink" />
        )}

        {!isUser && isStreaming && statusLabel && (
          <span className="mt-2.5 flex items-center gap-2.5 text-[11.5px] text-ink-muted">
            {!message.content && <ThinkingDots />}
            <svg viewBox="0 0 64 12" fill="none" aria-hidden="true" className="w-16 h-3 shrink-0">
              <path
                d="M2 8 C18 2, 40 10, 62 4"
                stroke="var(--fn-accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="2 7"
                className="animate-dot-walk"
              />
            </svg>
            <span>{statusLabel}</span>
          </span>
        )}

        {!isUser && message.options && onChooseOption && (
          <DecisionCards
            options={message.options}
            disabled={!!chatBusy}
            expired={!!optionsExpired}
            onChoose={(label) => onChooseOption(message.id, label)}
          />
        )}
      </div>
      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          className="self-start mt-1 inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-paper-hover"
        >
          <RefreshCw className="h-3 w-3" />
          Retry
        </button>
      )}
    </div>
  );
};

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  // Minimum visible rows when empty. The desktop slide-in panel has room
  // for a few lines; the compact mobile bottom bar stays at 1.
  minRows?: number;
  // "docked" is the design's composer for the desktop concierge strip: a
  // pill on the card surface with the send button as a sibling inside it.
  // "sheet" is the mobile chat sheet, which the artboards draw differently
  // and which this variant deliberately leaves alone.
  variant?: "sheet" | "docked";
}

const CHAT_INPUT_MAX_HEIGHT = 160;

const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      disabled,
      placeholder,
      onFocus,
      minRows = 1,
      variant = "sheet",
    },
    forwardedRef
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null);

    // Merge the forwarded ref (parent uses it for focus) with a local
    // ref we need for auto-grow.
    const setRefs = (el: HTMLTextAreaElement | null) => {
      localRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    // Auto-grow: never shorter than minRows, never taller than the cap,
    // otherwise fit the content. Resetting to "auto" first lets it shrink
    // back after a send. While the field is EMPTY no measurement happens
    // at all: the inline height is cleared and rows={minRows} governs.
    // Measuring an empty field is how the mobile sheet's composer once
    // mounted 160px tall: laid out at zero width for a moment, its
    // placeholder wrapped into a column, scrollHeight hit the cap, and the
    // inline height stayed. The fit also re-runs on resize (rotation, the
    // keyboard) so a width change never leaves a stale height behind.
    useEffect(() => {
      const el = localRef.current;
      if (!el) return;
      const fit = () => {
        if (!value) {
          el.style.height = "";
          return;
        }
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
      };
      fit();
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }, [value, minRows]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };
    // The design's docked composer: one pill on the card surface, the send
    // button a sibling inside it rather than floating over the field, and
    // no rule above it. The strip's own height is what separates it.
    if (variant === "docked") {
      return (
        <div className="pt-1.5 px-4 pb-3.5 shrink-0">
          <div
            className={`flex items-center gap-2.5 rounded-full border border-rule bg-white pl-4 pr-2 py-2 ${
              disabled ? "opacity-60" : ""
            }`}
          >
            <textarea
              ref={setRefs}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              placeholder={placeholder ?? "Ask anything about the trip..."}
              rows={minRows}
              disabled={disabled}
              aria-label="Message the concierge"
              className="flex-1 min-w-0 resize-none bg-transparent border-none outline-none font-serif text-[14px] leading-[1.35] text-ink placeholder:text-ink-faint overflow-y-auto"
            />
            <button
              onClick={onSend}
              disabled={!value.trim() || disabled}
              className="h-8 w-8 shrink-0 rounded-full bg-accent text-on-accent hover:bg-accent-hover transition-colors inline-flex items-center justify-center"
              aria-label="Send"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      );
    }

    // The mobile sheet's composer, per #m-trip-chat: the same pill as the
    // docked strip, on a hairline, with the send button a 44px sibling
    // holding the 38px accent disc. The field starts at one line and grows
    // with the text (capped). It is 16px, one pixel over the artboard's
    // 15px, because iOS Safari zooms into any smaller field on focus.
    return (
      <div className="border-t border-rule-hair px-4 pt-2 pb-[max(18px,env(safe-area-inset-bottom))] shrink-0 bg-background">
        <div
          className={`flex items-center gap-2.5 rounded-full border border-rule bg-white pl-4 pr-1.5 py-1.5 ${
            disabled ? "opacity-60" : ""
          }`}
        >
          <textarea
            ref={setRefs}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            placeholder={placeholder ?? "Ask anything about the trip..."}
            rows={minRows}
            disabled={disabled}
            aria-label="Message the concierge"
            className="flex-1 min-w-0 resize-none bg-transparent border-none outline-none py-[5px] font-serif text-base leading-[1.35] text-ink placeholder:text-ink-faint overflow-y-auto"
          />
          <button
            onClick={onSend}
            disabled={!value.trim() || disabled}
            className="h-11 w-11 -my-[3px] -mr-[3px] shrink-0 inline-flex items-center justify-center disabled:cursor-not-allowed"
            aria-label="Send"
          >
            <span className="h-[38px] w-[38px] rounded-full bg-accent text-on-accent inline-flex items-center justify-center">
              <ArrowUp className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
    );
  }
);
ChatInput.displayName = "ChatInput";

// ===== Itinerary subcomponents =====

// Every action-row button, so the four stay identical. 10px uppercase
// Archivo per the artboard, with a 44px target on touch where the row is
// always open.
// whitespace-nowrap: a label never breaks mid-word (the mobile artboard's
// own "Ask about it" wraps inside its button at 390). Four actions on one
// line down to a 360px phone needs 9px/.1em and 4px padding on mobile,
// half a pixel under the artboard's 9.5px/.12em; desktop is as drawn.
// Which of a place's photos a card shows. Only matters when several stops
// fall back to the same place (a neighbourhood, or the destination): a
// stable hash of the stop id spreads them across that place's photos
// instead of repeating one image down the page.
const photoIndexFor = (itemId: string): number => {
  let hash = 0;
  for (let i = 0; i < itemId.length; i++) hash = (hash * 31 + itemId.charCodeAt(i)) % 1000;
  return hash % 10;
};

const ACTION_BTN =
  "inline-flex items-center justify-center whitespace-nowrap gap-1 lg:gap-1.5 px-1 lg:px-2 min-h-[44px] lg:min-h-0 lg:py-2 font-sans text-[9px] lg:text-[10px] font-bold tracking-[0.1em] lg:tracking-[0.14em] uppercase text-ink-muted hover:text-ink transition-colors";

// Where a card's booking button goes, decided once so its label and its
// link cannot disagree. They used to: the label checked only for a
// booking reference while the link also needed the traveller's own URL,
// so a hotel booked as "ref ABC123" with no link read "Open booking" and
// opened a city-wide search. And every hotel read "Book / official site"
// while going to Booking.com search results, which is neither the
// official site nor that hotel's own page.
type BookingLinkKind = "confirmation" | "booking-search" | "venue";

const bookingLinkKind = (item: ItineraryItem): BookingLinkKind | null => {
  const hasUserUrl = !!item.bookingUrl;
  const isConfirmed = !!item.bookingReference?.trim();
  // The traveller's own reservation page.
  if (isConfirmed && hasUserUrl) return "confirmation";
  // A Booking.com search for the city and dates (see buildBookingSearchUrl
  // for why the city and not the hotel). The label must say so, because it
  // is an OTA's ranked results, not the place the concierge picked.
  if (item.type === "accommodation") return "booking-search";
  // A venue's own booking page: tickets, tours, tables.
  if (hasUserUrl) return "venue";
  return null;
};

const BOOKING_LINK_LABEL: Record<BookingLinkKind, string> = {
  confirmation: "Open booking",
  "booking-search": "Rooms on Booking.com",
  venue: "Book / official site",
};

interface ItineraryItemCardProps {
  item: ItineraryItem;
  // Cards never collapse (the artboard has no collapsed state); clicking one
  // selects its map pin instead.
  onSelect: () => void;
  // "thumb" is the flush square beside the text; "banner" is the full-bleed
  // strip above it.
  photoVariant?: "thumb" | "banner";
  onEdit: () => void;
  onAsk: () => void;
  onRemove: () => void;
  // The Note action: the card owns the sticky's editing state and hands
  // back the final text; an empty string removes the note.
  onSaveNote: (notes: string) => void;
  // Google does not know this stop's place id, so it can neither show a
  // photo nor open a listing. Dropping it puts the stop back in the queue
  // to be looked up again.
  onInvalidPlace: () => void;
  tripId: string;
  // For the Google Maps pill: the fallback location context when a stop
  // has no place id and no coordinates of its own.
  destination?: string;
  // Parent builds the real booking URL (it needs the trip's dates and
  // destination); the label comes from bookingLinkKind, the same decision
  // the URL is built from. Returns null when the item has nothing to book.
  getBookingHref: (item: ItineraryItem) => string | null;
}

const ItineraryItemCard = ({
  item,
  onSelect,
  photoVariant = "thumb",
  onEdit,
  onAsk,
  onRemove,
  onSaveNote,
  onInvalidPlace,
  tripId,
  destination,
  getBookingHref,
}: ItineraryItemCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  const isBooked = !!item.bookingReference?.trim();
  // See the note by the verified line: one field, two meanings.
  const isVerifiedSource = !!item.source && /^\d/.test(item.source.label.trim());
  const nights = computeNights(item.checkIn, item.checkOut);
  const stayLine =
    item.checkIn && item.checkOut
      ? `${formatShortDate(item.checkIn)} → ${formatShortDate(item.checkOut)}${
          nights ? ` · ${nights} ${nights === 1 ? "night" : "nights"}` : ""
        }`
      : null;
  const bookingHref = getBookingHref(item);
  const bookingKind = bookingLinkKind(item);

  // Sticky editing. null = not editing. The ref mirrors the state so the
  // blur that fires while the textarea unmounts on Escape sees the cancel.
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const noteDraftRef = useRef<string | null>(null);
  const noteButtonRef = useRef<HTMLButtonElement>(null);
  const noteTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // A remounted controlled textarea keeps its caret at 0, so an append
  // would prepend. Put the caret at the end once, when the editor opens.
  const noteOpen = noteDraft !== null;
  useEffect(() => {
    if (!noteOpen) return;
    const el = noteTextareaRef.current;
    if (!el) return;
    const n = el.value.length;
    el.setSelectionRange(n, n);
  }, [noteOpen]);
  const openNote = () => {
    noteDraftRef.current = item.notes ?? "";
    setNoteDraft(item.notes ?? "");
  };
  // Keyboard saves and cancels hand focus back to the Note action; a blur
  // is the user going elsewhere, so it must not pull focus back.
  const closeNote = (restoreFocus: boolean) => {
    noteDraftRef.current = null;
    setNoteDraft(null);
    if (restoreFocus) setTimeout(() => noteButtonRef.current?.focus(), 0);
  };
  const commitNote = (restoreFocus = false) => {
    const draft = noteDraftRef.current;
    if (draft === null) return;
    closeNote(restoreFocus);
    const text = draft.trim();
    if (text !== (item.notes ?? "")) onSaveNote(text);
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div
        data-item-id={item.id}
        onClick={onSelect}
        className="group/card relative bg-white border border-rule transition-[box-shadow,transform] duration-200 hover:shadow-[0_12px_32px_-12px_rgba(var(--fn-shadow-rgb),0.25)] hover:-translate-y-0.5 hover:bg-card-hover"
      >
        {(item.googlePlaceId || item.photoPlaceId) && photoVariant === "banner" && (
          <PlacePhoto
            googlePlaceId={item.googlePlaceId}
            photoPlaceId={item.photoPlaceId}
            photoIndex={photoIndexFor(item.id)}
            onInvalidPlace={onInvalidPlace}
            type={item.type}
            variant="banner"
            alt={item.title}
          />
        )}

        <div className="flex gap-[13px] p-[13px] pb-3 xl:gap-0 xl:p-0">
          {photoVariant === "thumb" && (
            <PlacePhoto
              googlePlaceId={item.googlePlaceId}
              photoPlaceId={item.photoPlaceId}
              photoIndex={photoIndexFor(item.id)}
              onInvalidPlace={onInvalidPlace}
              type={item.type}
              variant="thumb"
              alt={item.title}
            />
          )}
          <div className="min-w-0 flex-1 xl:pt-[18px] xl:px-[22px] xl:pb-4">
            {/* Time, kind, and the two badges all share one wrapping row. */}
            <div className="flex items-center gap-3 flex-wrap mb-1.5">
              <span className="font-sans text-[9.5px] lg:text-[10.5px] font-bold tracking-[0.14em] uppercase text-ink-faint">
                {item.specificTime || BUCKET_LABELS[item.timeBucket]}
                {" · "}
                {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
              </span>
              {/* The app stores one `source` for two things. A label opening
                  with a digit is a derived Google rating, shown here as the
                  verified badge; anything else is creator provenance and is
                  rendered as the handwritten line under the title. */}
              {isVerifiedSource && (
                <span className="font-sans text-[9.5px] lg:text-[10.5px] font-bold tracking-[0.14em] uppercase text-accent-text inline-flex items-center gap-1">
                  <BadgeCheck className="h-3 w-3 shrink-0" />
                  {item.source!.label}
                </span>
              )}
              {isBooked && (
                <span className="font-sans text-[9.5px] lg:text-[10.5px] font-bold tracking-[0.14em] uppercase text-accent-text inline-flex items-center gap-1">
                  <BadgeCheck className="h-3 w-3 shrink-0" />
                  Booked
                </span>
              )}
              {/* Grip. On mobile it rides at the end of this row, in flow and
                  negative-margined so it costs the text no width and can
                  never sit over the title. At lg it becomes the artboard's
                  26px badge straddling the card's left edge, fading in with
                  the card. */}
              <button
                {...attributes}
                {...listeners}
                aria-label="Reorder item"
                title="Drag to reorder"
                className="ml-auto shrink-0 -mr-2 -mt-2 -mb-2 h-11 w-11 inline-flex items-center justify-center text-ink-faint touch-none cursor-grab active:cursor-grabbing
                  lg:ml-0 lg:m-0 lg:absolute lg:top-3.5 lg:left-[-13px] lg:h-[26px] lg:w-[26px] lg:rounded-full lg:bg-paper lg:border lg:border-rule lg:opacity-0 lg:transition-opacity lg:group-hover/card:opacity-100 lg:group-focus-within/card:opacity-100"
              >
                <GripVertical className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="font-serif text-[17px] lg:text-[21px] leading-[1.25] text-ink mb-1">
              {item.title}
            </div>

            {item.source && !isVerifiedSource && (
              item.source.url ? (
                <a
                  href={buildClickUrl({
                    url: item.source.url,
                    tripId,
                    itemId: item.id,
                    kind: "source",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackAffiliateClick("source");
                  }}
                  className="font-hand text-[15px] lg:text-[17px] leading-none text-ink-soft hover:text-accent-text transition-colors inline-flex items-center gap-1.5 mb-1.5 -rotate-[1.2deg] origin-left"
                >
                  {item.source.label}
                  <ExternalLink className="h-[11px] w-[11px]" />
                </a>
              ) : (
                <span className="font-hand text-[15px] lg:text-[17px] leading-none text-ink-soft inline-flex mb-1.5 -rotate-[1.2deg] origin-left">
                  {item.source.label}
                </span>
              )
            )}

            {/* Below the title the artboard runs full-bleed on mobile rather
                than staying in the thumb's column: 62px thumb + 13px gap is
                pulled back so the copy starts at the card's own padding. */}
            <div className="-ml-[75px] xl:ml-0">
            {item.description && (
              <p className="text-[12.5px] lg:text-[13.5px] leading-[1.55] text-ink-muted mb-2.5">
                {item.description}
              </p>
            )}

            {stayLine && (
              <div className="flex items-center gap-[7px] text-[12.5px] text-ink-muted mb-2.5">
                <BedDouble className="h-[13px] w-[13px] shrink-0" />
                {stayLine}
              </div>
            )}

            {/* Address, hours, price and any confirmation ride one wrapping
                row rather than stacking, so a stop stays one glance. */}
            {(item.address || item.hours || item.priceRange || item.bookingReference) && (
              <div className="flex gap-4 flex-wrap text-[12px] text-ink-muted mb-0.5">
                {item.address && (
                  <span className="inline-flex items-center gap-[5px]">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {item.address}
                  </span>
                )}
                {item.hours && (
                  <span className="inline-flex items-center gap-[5px]">
                    <Clock className="h-3 w-3 shrink-0" />
                    {item.hours}
                  </span>
                )}
                {item.priceRange && (
                  <span className="inline-flex items-center gap-[5px]">
                    <Banknote className="h-3 w-3 shrink-0" />
                    {item.priceRange}
                  </span>
                )}
                {item.bookingReference && (
                  <span className="inline-flex items-center gap-[5px]">
                    <Check className="h-3 w-3 shrink-0 text-accent-text" />
                    <span className="font-mono">{item.bookingReference}</span>
                  </span>
                )}
              </div>
            )}

            {/* The artboard's pill row: the booking pill in ink, then the
                Google Maps pill as the outline "ext" variant. Every stop
                gets the Maps pill, since every stop is somewhere. */}
            <div className="flex gap-2.5 mt-2.5 flex-wrap items-center">
              {bookingHref && (
                <a
                  href={bookingHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackAffiliateClick("booking");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink text-ink px-4 py-[9px] min-h-[44px] lg:min-h-0 font-sans text-[11px] font-bold tracking-[0.12em] uppercase hover:bg-ink hover:text-paper transition-colors"
                >
                  <ExternalLink className="h-[11px] w-[11px]" />
                  {bookingKind ? BOOKING_LINK_LABEL[bookingKind] : "Book / official site"}
                </a>
              )}
              <a
                href={buildMapsPlaceUrl(item, destination)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-full border border-rule-dash-2 text-ink-muted px-4 py-[9px] min-h-[44px] lg:min-h-0 font-sans text-[11px] font-bold tracking-[0.12em] uppercase hover:border-ink hover:text-ink transition-colors"
              >
                <ExternalLink className="h-[11px] w-[11px]" />
                Google Maps
              </a>
            </div>
            </div>
          </div>
        </div>

        {/* The one grid-template-rows transition the brief allows: the action
            row reveals on hover at lg and up, and is always open on touch,
            where hover does not exist. */}
        <div className="grid grid-rows-[1fr] lg:grid-rows-[0fr] lg:group-hover/card:grid-rows-[1fr] lg:group-focus-within/card:grid-rows-[1fr] transition-[grid-template-rows] duration-[240ms] ease-out">
          <div className="overflow-hidden min-h-0">
            {/* flex-wrap is the safety net: should a device's fonts run wide,
                Remove drops to a second line instead of clipping. */}
            <div className="border-t border-rule-hair flex flex-wrap items-center gap-0.5 px-0.5 lg:pl-3 lg:pr-2.5 lg:py-1.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className={ACTION_BTN}
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAsk();
                }}
                className={ACTION_BTN}
              >
                <MessageCircle className="h-3 w-3" />
                Ask about it
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openNote();
                }}
                ref={noteButtonRef}
                className={ACTION_BTN}
              >
                <StickyNote className="h-3 w-3" />
                Note
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className={`${ACTION_BTN} ml-auto hover:!text-danger`}
              >
                <Trash2 className="h-3 w-3" />
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* The note is a sticky, not a caption: pinned off the card's right
          edge on desktop, tucked under it on mobile where there is no room
          to hang. */}
      {(item.notes || noteDraft !== null) && (
        <div className="mt-2 rotate-1 bg-sticky px-3.5 py-3 shadow-[0_8px_20px_-8px_rgba(var(--fn-shadow-rgb),0.3)] font-hand text-[18px] leading-[1.25] text-ink z-[2] lg:mt-0 lg:absolute lg:right-[-18px] lg:top-11 lg:w-[170px] lg:rotate-2">
          {noteDraft !== null ? (
            <textarea
              autoFocus
              rows={1}
              // Grows with the note, like the chat composer, so a long note
              // is never clipped inside the sticky while editing.
              ref={(el) => {
                noteTextareaRef.current = el;
                if (!el) return;
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }}
              value={noteDraft}
              aria-label="Your note"
              placeholder="scribble a note..."
              onChange={(e) => {
                noteDraftRef.current = e.target.value;
                setNoteDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  commitNote(true);
                } else if (e.key === "Escape") {
                  closeNote(true);
                }
              }}
              onBlur={() => commitNote(false)}
              className="block w-full resize-none overflow-hidden bg-transparent p-0 border-0 outline-none font-hand text-[18px] leading-[1.25] text-ink placeholder:text-ink-muted"
            />
          ) : (
            <span className="whitespace-pre-line">{item.notes}</span>
          )}
          <div className="mt-1.5 font-sans text-[9.5px] font-bold tracking-[0.14em] uppercase text-ink-muted">
            Your note
          </div>
        </div>
      )}
    </div>
  );
};

// Map components and the Maps URL helper live in src/components/TripMap.tsx
// so the SharedTrip page can reuse them.

// The contents rail: chapter 0 "Before you go", then one entry per day.
// Collapses to a 56px strip of numerals.
const ContentsRail = ({
  days, activePage, onSelect, essentialsCount, open, onToggleOpen, todayIndex,
}: {
  days: Day[];
  activePage: "essentials" | number;
  onSelect: (p: "essentials" | number) => void;
  essentialsCount: number;
  open: boolean;
  onToggleOpen: () => void;
  todayIndex: number | null;
}) => {
  // Collapsed strip: a 34px ring per chapter, filled with ink when current.
  const mini = (on: boolean) =>
    `h-[34px] w-[34px] shrink-0 rounded-full border font-serif italic text-sm inline-flex items-center justify-center ${
      on
        ? "border-ink bg-ink text-accent-on-dark"
        : "border-transparent text-ink-faint hover:text-ink"
    }`;

  if (!open) {
    return (
      <aside className="w-14 border-r border-rule shrink-0 flex flex-col items-center py-6 gap-1 overflow-y-auto">
        <button
          onClick={onToggleOpen}
          aria-label="Expand contents"
          className="p-2.5 mb-2 inline-flex text-ink-muted hover:text-ink"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={() => onSelect("essentials")}
          aria-label="Before you go"
          title="Before you go"
          className={mini(activePage === "essentials")}
        >
          <Compass className="h-3.5 w-3.5" />
        </button>
        {days.map((d, i) => (
          <button
            key={d.id}
            onClick={() => onSelect(i + 1)}
            aria-label={`Day ${i + 1}`}
            title={d.title || `Day ${i + 1}`}
            className={mini(activePage === i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </aside>
    );
  }

  // Written out rather than interpolated: Tailwind only generates classes it
  // can see as literal text in the source.
  const row = (on: boolean, align: "baseline" | "center" = "baseline") =>
    `flex ${align === "center" ? "items-center" : "items-baseline"} gap-2 w-full text-left px-2.5 py-[9px] rounded-[8px] text-[13.5px] ${
      on ? "bg-ink text-paper font-medium" : "text-ink-muted hover:text-ink"
    }`;

  // The dotted leader that carries the eye across to the count or marker.
  // On the current chapter it runs over ink, so it switches to paper at 35%.
  const leader = (on: boolean) =>
    `flex-1 border-b border-dotted ${
      on ? "border-[rgba(var(--fn-paper-rgb),0.35)]" : "border-rule-dash-2"
    }`;

  const onEssentials = activePage === "essentials";

  return (
    <aside className="w-[250px] border-r border-rule shrink-0 flex flex-col gap-0.5 overflow-y-auto pt-7 px-5 pb-6">
      <div className="flex items-center justify-between mb-3.5 px-1">
        <span className="font-sans text-[11px] font-bold tracking-[0.2em] uppercase leading-[1.2] text-ink-faint">
          Contents
        </span>
        <button
          onClick={onToggleOpen}
          aria-label="Collapse contents"
          className="p-1.5 -m-1.5 inline-flex text-ink-faint hover:text-ink"
        >
          <ChevronLeft className="h-[15px] w-[15px]" />
        </button>
      </div>

      <button
        onClick={() => onSelect("essentials")}
        className={row(onEssentials, "center")}
      >
        <span
          className={`inline-flex shrink-0 ${
            onEssentials ? "text-accent-on-dark" : "text-ink-faint"
          }`}
        >
          <Compass className="h-3.5 w-3.5" />
        </span>
        Before you go
        <span className={leader(onEssentials)} />
        <span className="font-serif italic text-[12px] shrink-0">
          {essentialsCount || ""}
        </span>
      </button>

      {days.map((d, i) => {
        const on = activePage === i + 1;
        return (
          <button key={d.id} onClick={() => onSelect(i + 1)} className={row(on)}>
            <span
              className={`font-serif italic shrink-0 ${
                on ? "text-accent-on-dark" : "text-ink-faint"
              }`}
            >
              {i + 1}
            </span>
            <span className="min-w-0 truncate">{d.title || `Day ${i + 1}`}</span>
            <span className={leader(on)} />
            {todayIndex === i && (
              <span className="text-[10px] text-accent-on-dark shrink-0">
                today
              </span>
            )}
          </button>
        );
      })}

      {days.length === 0 && (
        <span className="mt-0.5 flex items-baseline gap-2 px-2.5 py-[9px] border border-dashed border-rule-dash-2 rounded-[8px] text-ink-faint text-[13.5px]">
          <span className="font-serif italic">1</span>
          Day 1
          <span className="flex-1 border-b border-dotted border-rule-dash-2" />
        </span>
      )}
    </aside>
  );
};

// One day, as a chapter: kicker, underlined title, then the stops on a dotted
// timeline. The design puts time-of-day on each card's kicker rather than
// grouping stops under bucket headings.
// The label of the dashed open-slot prompt that closes each day. The
// artboard's labels name the first stretch of the day with nothing in it
// ("Late evening is open, ask for ideas"); when every bucket has a stop,
// the fallback line is ours, not the designer's.
const OPEN_SLOT_COPY: Record<TimeBucket, string> = {
  morning: "Morning open",
  afternoon: "Afternoon open",
  evening: "Evening open",
  night: "Late evening is open",
};
const openSlotLabel = (day: Day): string => {
  const open = VALID_BUCKETS.find(
    (b) => !day.items.some((i) => i.timeBucket === b)
  );
  return open
    ? `${OPEN_SLOT_COPY[open]}, ask for ideas`
    : "Add another stop, ask for ideas";
};

// "From the day · notes", per the artboard: the ✳ Caveat list and an "Add
// note" link, nothing else. Editing is inline so the resting panel stays
// as drawn: Enter saves, Escape cancels, blur saves, tapping a note reopens
// it, and saving it empty removes it. The rows and the link swallow
// mousedown so a tap while an edit is open commits it in the click handler
// (where the indices are known) instead of racing the blur.
const DayNotesPanel = ({
  notes,
  onChange,
}: {
  notes: string[];
  onChange: (notes: string[]) => void;
}) => {
  // index null = a new note at the end of the list. The ref mirrors the
  // state so the blur that fires as the input unmounts sees a cancel.
  const [editing, setEditing] = useState<{ index: number | null; value: string } | null>(null);
  const editingRef = useRef(editing);
  const edit = (next: { index: number | null; value: string } | null) => {
    editingRef.current = next;
    setEditing(next);
  };
  // After a keyboard save or cancel, focus lands on the row that replaced
  // the input (or on "Add note" when nothing did). A blur is the user going
  // elsewhere, so it sets no target. undefined = nothing to restore.
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const addRef = useRef<HTMLButtonElement>(null);
  const focusAfter = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (editing !== null || focusAfter.current === undefined) return;
    const target = focusAfter.current;
    focusAfter.current = undefined;
    (target === null ? addRef.current : rowRefs.current[target])?.focus();
  }, [editing, notes]);
  const commit = (restoreFocus = false) => {
    const cur = editingRef.current;
    if (!cur) return;
    edit(null);
    const text = cur.value.trim();
    const next = [...notes];
    if (cur.index === null) {
      if (text) next.push(text);
    } else if (text) {
      next[cur.index] = text;
    } else {
      next.splice(cur.index, 1);
    }
    if (restoreFocus) {
      focusAfter.current =
        cur.index === null ? (text ? next.length - 1 : null) : text ? cur.index : null;
    }
    if (next.length !== notes.length || next.some((n, i) => n !== notes[i])) {
      onChange(next);
    }
  };
  const noteInput = (value: string, key: string) => (
    <div key={key} className="flex items-baseline gap-2 lg:gap-[9px]">
      <span className="text-accent-text" aria-hidden="true">✳</span>
      <input
        autoFocus
        value={value}
        aria-label="Note"
        placeholder="scribble a note..."
        onChange={(e) =>
          edit({ index: editingRef.current?.index ?? null, value: e.target.value })
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            commit(true);
          } else if (e.key === "Escape") {
            focusAfter.current = editingRef.current?.index ?? null;
            edit(null);
          }
        }}
        onBlur={() => commit(false)}
        className="flex-1 min-w-0 bg-transparent p-0 border-0 border-b border-rule-dash-2 outline-none font-hand text-[17px] lg:text-[19px] text-ink placeholder:text-ink-faint"
      />
    </div>
  );

  return (
    <div className="relative mb-4 lg:mb-5">
      <span className="absolute -left-6 top-3 h-[18px] w-[18px] lg:-left-9 lg:top-3.5 lg:h-5 lg:w-5 rounded-full bg-paper border-2 border-rule-dash-2 box-border" />
      <div className="bg-paper-deep border border-rule p-3.5 lg:px-5 lg:py-4">
        {/* On mobile the 44px "Add note" box extends 20px up and 8px down,
            so it ends exactly where the list starts (mb-2) and never sits on
            the first note; z-[1] keeps it on top should the two ever touch. */}
        <div className="relative z-[1] flex items-baseline justify-between mb-2 lg:mb-2.5">
          <span className="font-sans text-[9.5px] lg:text-[10px] font-bold leading-[normal] tracking-[0.18em] lg:tracking-[0.2em] uppercase text-ink-faint">
            From the day · notes
          </span>
          <button
            type="button"
            ref={addRef}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              commit();
              edit({ index: null, value: "" });
            }}
            className="inline-flex items-center gap-1 h-11 pl-2 -mt-5 -mb-2 lg:h-auto lg:pl-0 lg:py-1.5 lg:my-0 font-sans text-[9px] lg:text-[9.5px] font-bold leading-[normal] tracking-[0.14em] uppercase text-accent-text"
          >
            <Plus className="h-[11px] w-[11px]" />
            Add note
          </button>
        </div>
        {/* Mobile hit areas: rows must not overlap (a tap on a row's lower
            third would open the next note), so only the first row extends
            up into the header gap and the last down into the padding; the
            rest are exactly their own line. The pitch stays the artboard's. */}
        <div className="flex flex-col font-hand text-[17px] leading-[1.45] lg:text-[19px] lg:leading-[1.5] text-ink-soft">
          {notes.map((note, i) =>
            editing && editing.index === i ? (
              noteInput(editing.value, `edit-${i}`)
            ) : (
              <button
                key={`note-${i}`}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  // If the open edit is an earlier note being emptied, the
                  // commit removes it and this row moves up one.
                  const cur = editingRef.current;
                  const shiftsUp =
                    !!cur && cur.index !== null && cur.index < i && cur.value.trim() === "";
                  commit();
                  edit({ index: shiftsUp ? i - 1 : i, value: note });
                }}
                aria-label={`Edit note: ${note}`}
                className="flex w-full items-baseline gap-2 lg:gap-[9px] text-left first:pt-2.5 first:-mt-2.5 last:pb-2.5 last:-mb-2.5 lg:first:pt-0 lg:first:mt-0 lg:last:pb-0 lg:last:mb-0"
              >
                <span className="text-accent-text" aria-hidden="true">✳</span>
                <span>{note}</span>
              </button>
            )
          )}
          {editing && editing.index === null && noteInput(editing.value, "new")}
          {notes.length === 0 && !editing && (
            <div className="text-ink-faint text-[15px] lg:text-[17px]">
              nothing scribbled yet
              <span className="hidden lg:inline">. Notes from the chat land here.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// The dashed prompt that closes the day. Desktop rounds it 4px; the mobile
// artboard draws it square.
const OpenSlotButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <div className="relative mb-[18px] lg:mb-0">
    <span className="absolute -left-6 top-2.5 h-[18px] w-[18px] lg:-left-9 lg:h-5 lg:w-5 rounded-full bg-paper border-2 border-dashed border-rule-dash-2 box-border" />
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 border border-dashed border-rule-dash-2 lg:rounded-[4px] p-4 lg:p-[15px] font-sans text-[10px] lg:text-[12px] font-bold leading-[normal] tracking-[0.14em] uppercase text-ink-faint hover:border-accent hover:text-accent-text transition-colors"
    >
      <Plus className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
      {label}
    </button>
  </div>
);

const DayChapter = ({
  day, index, total, onSelectItem, onEdit, onAsk, onRemove,
  onSaveItemNote, onSaveDayNotes, onAskOpenSlot, onInvalidPlace,
  onDragEnd, tripId, destination, getBookingHref, onPrev, onNext, building,
}: {
  day: Day;
  index: number;
  total: number;
  onSelectItem: (id: string) => void;
  onEdit: (item: ItineraryItem) => void;
  onAsk: (item: ItineraryItem) => void;
  onRemove: (itemId: string) => void;
  onSaveItemNote: (itemId: string, notes: string) => void;
  onSaveDayNotes: (dayId: string, notes: string[]) => void;
  onAskOpenSlot: (day: Day, index: number, label: string) => void;
  onInvalidPlace: (itemId: string) => void;
  onDragEnd: (e: DragEndEvent) => void;
  tripId: string;
  destination?: string;
  getBookingHref: (item: ItineraryItem) => string | undefined;
}  & { onPrev: () => void; onNext: () => void; building?: boolean }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const navBtn = (enabled: boolean) =>
    `h-9 w-9 rounded-full inline-flex items-center justify-center border ${
      enabled ? "border-ink text-ink" : "border-rule-soft text-rule-dash-2 cursor-default"
    }`;

  return (
    <div className="px-[22px] md:px-14 pt-8 pb-10 md:pt-12 md:pb-16">
      <div className="flex items-baseline justify-between gap-4">
        <div className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase lg:text-[11px] lg:tracking-[0.22em] text-accent-text mb-1.5 lg:mb-2.5">
          Day {index + 1} · {formatDayDate(day.date)}
        </div>
        <div className="hidden md:flex gap-2.5">
          <button onClick={onPrev} disabled={index === 0} aria-label="Previous day" className={navBtn(index > 0)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button onClick={onNext} disabled={index >= total - 1} aria-label="Next day" className={navBtn(index < total - 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <h1 className="relative inline-block font-serif tracking-normal text-[32px] leading-[1.12] md:text-[46px] md:leading-[1.1] mb-2">
        {day.title || `Day ${index + 1}`}
        <svg viewBox="0 0 300 14" fill="none" aria-hidden="true" className="absolute left-0 -bottom-2 md:-bottom-2.5 w-full h-3 md:h-3.5">
          <path d="M4 9 C60 3, 150 11, 296 5" stroke="var(--fn-accent)" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </h1>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={day.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {/* Mobile per the artboard: a 24px timeline column with 18px rings
              (left:-24px); desktop keeps its 36px column and 20px rings. The
              dotted line sits at 9px in both, on the rings' centre. */}
          <div className="relative pl-6 lg:pl-9 mt-11">
            <span className="absolute left-[9px] top-2 bottom-2 border-l-2 border-dotted border-rule-dash-2" />
            {day.items.length === 0 && (
              <p className="text-ink-muted font-serif text-[15px] py-6">
                Nothing on this day yet. Ask the concierge to fill it in.
              </p>
            )}
            {day.items.map((item) => (
              <div key={item.id} className={`relative mb-[22px] ${building ? "animate-fade-in" : ""}`}>
                <span className="absolute -left-6 top-3.5 h-[18px] w-[18px] lg:-left-9 lg:h-5 lg:w-5 rounded-full bg-paper border-2 border-accent box-border flex items-center justify-center">
                  <span className="h-[7px] w-[7px] lg:h-2 lg:w-2 rounded-full bg-accent" />
                </span>
                <ItineraryItemCard
                  item={item}
                  onSelect={() => onSelectItem(item.id)}
                  onEdit={() => onEdit(item)}
                  onAsk={() => onAsk(item)}
                  onRemove={() => onRemove(item.id)}
                  onSaveNote={(text) => onSaveItemNote(item.id, text)}
                  onInvalidPlace={() => onInvalidPlace(item.id)}
                  tripId={tripId}
                  destination={destination}
                  getBookingHref={getBookingHref}
                />
              </div>
            ))}

            {/* While the concierge is still placing stops, the next slot is
                marked rather than left blank. */}
            {building && (
              <div className="relative mb-5">
                <span className="absolute -left-6 top-2.5 h-[18px] w-[18px] lg:-left-9 lg:h-5 lg:w-5 rounded-full bg-paper border-2 border-dashed border-accent box-border" />
                <div className="border border-dashed border-rule-dash-2 rounded-[4px] px-5 py-4 flex items-center gap-3 font-hand text-[18px] leading-[1.3] text-ink-faint">
                  <svg viewBox="0 0 64 12" fill="none" aria-hidden="true" className="w-16 h-3 shrink-0">
                    <path
                      d="M2 8 C18 2, 40 10, 62 4"
                      stroke="var(--fn-accent)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="2 7"
                      className="animate-dot-walk"
                    />
                  </svg>
                  placing the next stop...
                </div>
              </div>
            )}

            {/* Per the artboard, the day closes with its notes panel and the
                open-slot prompt; both wait while the concierge is building. */}
            {!building && (
              <>
                <DayNotesPanel
                  notes={day.notes ?? []}
                  onChange={(notes) => onSaveDayNotes(day.id, notes)}
                />
                <OpenSlotButton
                  label={openSlotLabel(day)}
                  onClick={() => onAskOpenSlot(day, index, openSlotLabel(day))}
                />
              </>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};


// ===== Main page component =====
const Trip = () => {
  const { id } = useParams();
  const location = useLocation();
  const initialPromptFromState =
    (location.state as { initialPrompt?: string } | null)?.initialPrompt ?? null;
  const initialPromptConsumed = useRef(false);
  // Composer preset: sent to the server ONCE, with the first message, so
  // it can inject that preset's verified starter picks into the system
  // prompt (the picks themselves live server-side; see api/_preset-picks.js).
  const pendingPresetId = useRef<string | null>(
    (location.state as { presetId?: string } | null)?.presetId ?? null
  );
  const isDemo = id === "demo";
  const {
    journeys,
    journeysLoading,
    getJourney,
    updateJourney,
    createEmptyJourney,
  } = useJourneys();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const { preferences } = usePreferences();
  const [isSharing, setIsSharing] = useState(false);

  const [demoTrip, setDemoTrip] = useState<Trip>(MOCK_TRIP);

  // Create-empty-if-absent, guarded to run AT MOST ONCE per trip id. Two
  // hazards this avoids:
  // 1. Wait for journeysLoading=false before deciding the id is missing, so a
  //    signed-in refresh doesn't create a blank trip that overwrites the real
  //    one mid server-load.
  // 2. Once we've made the create/skip decision for this id, never re-run it.
  //    Without the ref guard, journeysLoading flipping true->false mid-build
  //    re-fired this effect; a momentary getJourney miss then created a fresh
  //    empty trip ON TOP of the one being built, dropping all its items.
  const createDecisionMadeFor = useRef<string | null>(null);
  useEffect(() => {
    if (isDemo || !id) return;
    if (journeysLoading) return;
    if (createDecisionMadeFor.current === id) return;
    createDecisionMadeFor.current = id;
    if (!getJourney(id)) {
      createEmptyJourney(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, id, journeysLoading]);

  const trip: Trip | undefined = isDemo
    ? demoTrip
    : id
    ? getJourney(id)
    : undefined;

  // A primitive the backfill effect can depend on without re-running every
  // time a stop's coordinates or notes change.
  const tripId = trip?.id;
  const tripRef = useRef<Trip | undefined>(trip);
  useEffect(() => {
    tripRef.current = trip;
  }, [trip]);

  const setTrip = useCallback(
    (updater: (prev: Trip) => Trip) => {
      if (isDemo) {
        setDemoTrip((prev) => updater(prev));
      } else if (id) {
        updateJourney(id, updater);
      }
    },
    [isDemo, id, updateJourney]
  );

  const [draft, setDraft] = useState("");
  // Map selection: which item's pin is highlighted (and scrolled to).
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<
    "itinerary" | "map" | "inspiration"
  >("itinerary");
  // Right column on desktop toggles between Map and Inspiration.
  const [desktopRightTab, setDesktopRightTab] = useState<"map" | "inspiration">(
    "map"
  );
  // Track whether we've already auto-flipped to Inspiration on this trip
  // so we only do it once per trip (when the destination first appears).
  const inspirationAutoFlipped = useRef(false);
  // Guards the one-per-trip "trip_built" funnel event (first structure added).
  const tripBuiltTracked = useRef(false);
  // Mobile chat panel is a two-state collapsible (peek vs expanded).
  // Peek (~200px) leaves the canvas in view; expanded covers the canvas
  // for a chat-focused mode. No drag mechanics — tap to toggle.
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  // Desktop: chat is a slide-in panel (not a permanent column), so the
  // itinerary + map get the full canvas. Open by default on an empty
  // trip (the user needs the chat to start), collapsed once there are
  // days to look at.
  const [isDesktopChatOpen, setIsDesktopChatOpen] = useState(false);
  // The rail pages the canvas one chapter at a time: "essentials" is chapter
  // 0 ("Before you go"), then a 1-based day number.
  const [activePage, setActivePage] = useState<"essentials" | number>(1);
  // Reported up by <Inspiration>, so the tab meta, the mobile entry row and
  // the sheet header can all show a live video count. The desktop panel and
  // the mobile sheet are separate mounts with separate pasted-video state,
  // and both stay mounted, so each reports into its own slot. Sharing one
  // number would let the hidden surface overwrite the visible one's count.
  // null means "no Inspiration mounted yet, so we genuinely do not know":
  // the feed is only fetched once its surface opens, and showing 0 before
  // then would be a wrong number rather than a missing one.
  const [inspirationCounts, setInspirationCounts] = useState<{
    desktop: number | null;
    mobile: number | null;
  }>({ desktop: null, mobile: null });
  const reportDesktopVideoCount = useCallback(
    (n: number | null) =>
      setInspirationCounts((prev) => ({ ...prev, desktop: n })),
    []
  );
  const reportMobileVideoCount = useCallback(
    (n: number | null) =>
      setInspirationCounts((prev) => ({ ...prev, mobile: n })),
    []
  );
  // The mobile sheet unmounts when it closes, so its last count would sit
  // there unchallenged after the destination changes. Reset both slots and
  // let whichever surface is mounted report the new destination's number.
  useEffect(() => {
    setInspirationCounts({ desktop: null, mobile: null });
  }, [trip?.destination]);
  const [tocOpen, setTocOpen] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState<string | null>(null);
  const [failedMsgId, setFailedMsgId] = useState<string | null>(null);
  // Human-readable status of what the AI is currently doing, shown in the
  // streaming bubble before text arrives. Cleared when text starts/stream ends.
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  // Access token (when signed in) so the budget is keyed per-user, not
  // per-IP, both for the meter and for the concierge request below.
  const { session } = useAuth();
  const accessToken = session?.access_token;
  // Daily message-budget meter ("N messages left today"). Refetched after
  // each message so the ambient counter stays current.
  const usage = useUsage(accessToken);
  // Mirrors UsageMeter's guard: an unknown (Infinity) or unconfigured (0)
  // budget must never read as exhausted.
  const atMessageLimit =
    Number.isFinite(usage.remaining) && usage.limit > 0 && usage.remaining <= 0;
  const [destinationCenter, setDestinationCenter] = useState<
    { lat: number; lng: number } | null
  >(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  // Desktop chat lives in a separate slide-in panel that's mounted at the
  // same time as the mobile panel, so it needs its own refs (a single ref
  // can't bind to two elements).
  const desktopInputRef = useRef<HTMLTextAreaElement>(null);
  const desktopChatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll whichever chat surface is mounted/visible. Both refs exist
    // in the DOM; scrolling both is harmless (the hidden one is a no-op).
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    desktopChatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [trip?.chatHistory.length, streamingMsgId]);

  // Auto-expand the chat panel when AI starts streaming a reply, so the
  // user actually sees the response rather than just the input bar.
  // Applies to both the mobile panel and the desktop slide-in.
  useEffect(() => {
    if (isStreaming) {
      setIsChatExpanded(true);
      setIsDesktopChatOpen(true);
    }
  }, [isStreaming]);

  // Open the desktop chat by default while the trip is empty (the user
  // needs it to start building). Once there are days, it stays where the
  // user left it. Fires once per trip on first load.
  const desktopChatInitialized = useRef(false);
  useEffect(() => {
    if (desktopChatInitialized.current || !trip) return;
    desktopChatInitialized.current = true;
    setIsDesktopChatOpen(trip.days.length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  // Funnel: opening an EXISTING (non-empty) trip is a returning-usage
  // signal, distinct from a fresh build. Fire once per trip mount when it
  // already has days.
  const returningTripTracked = useRef(false);
  useEffect(() => {
    if (returningTripTracked.current || isDemo || !trip) return;
    returningTripTracked.current = true;
    if (trip.days.length > 0) trackReturningTripOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  // Same for mobile: a brand-new empty trip should open the chat sheet
  // immediately (greeting + input visible), instead of the bare empty
  // state behind the FAB. Existing trips stay collapsed so the canvas
  // leads. Fires once per trip.
  const mobileChatInitialized = useRef(false);
  useEffect(() => {
    if (mobileChatInitialized.current || !trip) return;
    mobileChatInitialized.current = true;
    if (trip.days.length === 0) setIsChatExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  useEffect(() => {
    if (!trip?.destination) {
      setDestinationCenter(null);
      return;
    }
    let cancelled = false;
    geocodeAddress(trip.destination).then((coords) => {
      if (!cancelled) setDestinationCenter(coords);
    });
    return () => {
      cancelled = true;
    };
  }, [trip?.destination]);

  useEffect(() => {
    if (!expandedItemId) return;
    const el = document.querySelector(
      `[data-item-id="${expandedItemId}"]`
    );
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [expandedItemId]);

  useEffect(() => {
    if (trip && trip.days.length === 0) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  // NOTE: do NOT early-return here. Several hooks (useCallback/useEffect/
  // useMemo) run below; returning before them changes the hook count between
  // renders and throws React #300 ("rendered fewer hooks than expected") if
  // `trip` ever becomes null mid-session (e.g. the trips list briefly empties
  // on a failed load). The `if (!trip)` guard now lives just before the main
  // return, after all hooks. Hooks below must be null-safe via `trip?.`.

  const focusChatInput = () => {
    // Both inputs are in the DOM but only one is visible per viewport
    // (the other is display:none via lg: classes). focus() on a hidden
    // element is a no-op, so focusing both reliably focuses the live one.
    setTimeout(() => {
      desktopInputRef.current?.focus();
      inputRef.current?.focus();
    }, 0);
  };

  // Cards are always fully open, so a click selects rather than toggles:
  // it highlights this stop's map pin, and clicking the selected one again
  // clears it.
  const handleSelectItem = (itemId: string) => {
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const handleEditItem = (item: ItineraryItem) => {
    setDraft(`Change ${item.title}: `);
    setExpandedItemId(null);
    setIsChatExpanded(true);
    setIsDesktopChatOpen(true);
    focusChatInput();
  };

  const handleAskItem = (item: ItineraryItem) => {
    setDraft(`Tell me more about ${item.title}: `);
    setExpandedItemId(null);
    setIsChatExpanded(true);
    setIsDesktopChatOpen(true);
    focusChatInput();
  };

  // The open-slot prompt, verbatim from the artboard's askOpenSlot:
  // "Ideas for art & coffee, late evening is open: ".
  const handleAskOpenSlot = (day: Day, index: number, label: string) => {
    const title = (day.title || `day ${index + 1}`).toLowerCase();
    const clause = label.split(",")[0].toLowerCase();
    setDraft(`Ideas for ${title}, ${clause}: `);
    setExpandedItemId(null);
    setIsChatExpanded(true);
    setIsDesktopChatOpen(true);
    focusChatInput();
  };

  const handleSaveItemNote = (itemId: string, notes: string) => {
    setTrip((prev) => ({
      ...prev,
      days: prev.days.map((d) => ({
        ...d,
        items: d.items.map((it) =>
          it.id === itemId ? { ...it, notes: notes || undefined } : it
        ),
      })),
    }));
  };

  const handleSaveDayNotes = (dayId: string, notes: string[]) => {
    setTrip((prev) => ({
      ...prev,
      days: prev.days.map((d) =>
        d.id === dayId ? { ...d, notes: notes.length ? notes : undefined } : d
      ),
    }));
  };

  const handleRemoveItem = (itemId: string) => {
    setTrip((prev) => ({
      ...prev,
      days: prev.days.map((day) => ({
        ...day,
        items: day.items.filter((i) => i.id !== itemId),
      })),
    }));
    setExpandedItemId((prev) => (prev === itemId ? null : prev));
  };

  const handlePinClick = (itemId: string) => {
    // Highlight the pin and scroll to the card. Cards are always fully open
    // now, so there is nothing to un-minimize first.
    setExpandedItemId(itemId);
  };

  const handleClearSelection = useCallback(() => {
    setExpandedItemId(null);
  }, []);

  // Compute the click-tracked booking URL for an item. The interesting
  // logic is what underlying URL we use BEFORE wrapping through
  // /api/click:
  //  - Confirmed user reservation (has bookingReference + bookingUrl)
  //    → use the user's URL as-is (their actual booking page)
  //  - Accommodation suggestion → build a Booking.com search URL with
  //    the hotel name + city + check-in/check-out. The AI used to
  //    generate per-hotel URLs but they often 404'd; search URLs always
  //    land on a working page with the filters pre-applied.
  //  - Anything else with an AI-provided bookingUrl → use as-is.
  // Backfill Google place ids for stops that lack one. The concierge only
  // calls verify_place for named businesses, so walks, generic dining and
  // every stop in a trip built before verification existed carry no id,
  // and without an id a card cannot open its Maps listing or show a photo.
  // The endpoint answers null when it is not confident, and the client
  // never re-asks about a stop it has already had an answer for.
  //
  // The effect deliberately does NOT depend on the trip object. It writes
  // ids back into that same object, and trailing geocode writes land for
  // seconds after a build, so depending on it would abort the loop's own
  // next batch on nearly every pass. It reads the current stops from
  // tripRef each round instead, and the id list only settles once.
  const resolveAttempted = useRef<Set<string>>(new Set());
  // Bumped when a stop's place id turns out to be one Google does not
  // know, so the backfill wakes up and looks that stop up again.
  const [resolveNonce, setResolveNonce] = useState(0);
  const handleInvalidPlace = useCallback(
    (itemId: string) => {
      resolveAttempted.current.delete(itemId);
      setTrip((prev) => ({
        ...prev,
        days: prev.days.map((d) => ({
          ...d,
          items: d.items.map((it) =>
            it.id === itemId && it.googlePlaceId
              ? { ...it, googlePlaceId: undefined }
              : it
          ),
        })),
      }));
      setResolveNonce((n) => n + 1);
    },
    [setTrip]
  );

  useEffect(() => {
    if (!tripId || isStreaming) return;
    const ctrl = new AbortController();
    let cancelled = false;

    const nextBatch = () => {
      const current = tripRef.current;
      if (!current?.destination?.trim()) return [];
      return current.days
        .flatMap((d) => d.items)
        .filter(
          (i) =>
            !i.googlePlaceId &&
            !i.photoPlaceId &&
            !!i.title?.trim() &&
            !resolveAttempted.current.has(i.id)
        )
        .slice(0, 8);
    };

    (async () => {
      for (;;) {
        if (cancelled) return;
        const batch = nextBatch();
        if (!batch.length) return;
        const destination = tripRef.current?.destination?.trim();
        if (!destination) return;

        let results: Record<
          string,
          { placeId?: string; photoPlaceId?: string } | null
        >;
        try {
          const res = await fetch("/api/resolve-place", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              destination,
              items: batch.map((item) => ({
                id: item.id,
                title: item.title,
                address: item.address,
              })),
            }),
            signal: ctrl.signal,
          });
          // Rate limited, misconfigured or offline: stop without marking
          // anything, so these stops stay eligible on the next visit
          // rather than being written off for the session.
          if (!res.ok) return;
          results = (await res.json().catch(() => ({})))?.results ?? {};
        } catch {
          return;
        }
        if (cancelled) return;

        // Only now are these stops answered for: a resolved id, or a
        // considered "no confident match". Marking before the response
        // would strand a batch that never came back.
        batch.forEach((item) => resolveAttempted.current.add(item.id));

        // A placeId is the stop itself and drives its Maps link too. A
        // photoPlaceId is a stand-in (its area, or the destination) and
        // only ever fills the picture.
        const found = new Map<string, { googlePlaceId?: string; photoPlaceId?: string }>();
        for (const item of batch) {
          const answer = results[item.id];
          if (answer?.placeId) found.set(item.id, { googlePlaceId: answer.placeId });
          else if (answer?.photoPlaceId) found.set(item.id, { photoPlaceId: answer.photoPlaceId });
        }
        if (!found.size) continue;
        setTrip((prev) => ({
          ...prev,
          days: prev.days.map((d) => ({
            ...d,
            items: d.items.map((it) =>
              !it.googlePlaceId && !it.photoPlaceId && found.has(it.id)
                ? { ...it, ...found.get(it.id) }
                : it
            ),
          })),
        }));
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [tripId, isStreaming, setTrip, resolveNonce]);

  const getBookingHref = useCallback(
    (item: ItineraryItem): string | null => {
      const kind = bookingLinkKind(item);
      if (!kind) return null;
      const targetUrl =
        kind === "booking-search"
          ? buildBookingSearchUrl({
              hotelName: item.title,
              destination: trip?.destination,
              checkIn: item.checkIn || trip?.startDate,
              checkOut: item.checkOut || trip?.endDate,
            })
          : item.bookingUrl!;
      return buildClickUrl({
        url: targetUrl,
        tripId: trip?.id,
        itemId: item.id,
        kind: "booking",
      });
    },
    [trip?.id, trip?.destination, trip?.startDate, trip?.endDate]
  );

  // Auto-flip to the Inspiration tab the moment a destination first
  // appears on this trip — gives users the "trending content just showed
  // up" moment without requiring them to discover the tab. Only fires
  // once per trip; users can switch away and back without it forcing.
  useEffect(() => {
    if (inspirationAutoFlipped.current) return;
    if (!trip?.destination?.trim()) return;
    inspirationAutoFlipped.current = true;
    setDesktopRightTab("inspiration");
    // Deliberately NOT on mobile any more. This used to flip a tab, which was
    // a peer of the itinerary; mobile now opens Inspiration as a full-height
    // sheet, so auto-flipping would hijack the page the moment a destination
    // appeared. Mobile discovers it through the entry row instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.destination]);

  // Reset the auto-flip flag when the trip id changes (navigating to a
  // different trip should get a fresh chance to auto-flip).
  useEffect(() => {
    inspirationAutoFlipped.current = false;
  }, [trip?.id]);

  // Plain arrow (not memoized) so handleSend's closure stays fresh each
  // render. handleSend is declared later in the component; this body is
  // only evaluated when the user clicks Add, by which point handleSend
  // is in scope.
  const handleInspirationAdd = ({
    place,
    video,
    targetDate,
  }: {
    place: ExtractedPlace;
    video: InspirationVideo;
    targetDate: string | null;
  }) => {
    const channel = video.channelTitle?.trim() || "a creator on YouTube";
    const url = `https://www.youtube.com/watch?v=${video.videoId}`;
    const dayClause = targetDate
      ? ` Add it to the day on ${targetDate}.`
      : "";
    const msg = `Add "${place.name}" to my trip.${dayClause} It's a ${place.type} place I found in ${channel}'s YouTube video: ${url}\n\nUse "From ${channel}" with that URL as the source. Verify it on Google Places first.`;
    trackPlaceAddedFromInspiration();
    handleSend(msg);
    // No toast. The card now carries the whole outcome: it shows "Checking
    // Google Places" while the check runs and either "Added" or "Couldn't
    // verify" when it answers, so the user never has to look at the chat to
    // find out whether their tap worked. This only runs once verification
    // has already passed, so the chat message is a side effect of a
    // confirmed add, not the feedback channel.
  };

  // Day options for the Inspiration add-to-day picker.
  const inspirationDayOptions = useMemo(
    () =>
      (trip?.days ?? []).map((d, i) => ({
        id: d.id,
        n: i + 1,
        title: d.title || `Day ${i + 1}`,
        date: d.date,
        label: `Day ${i + 1} · ${formatDayDate(d.date)}`,
      })),
    [trip?.days]
  );

  const handleShare = async () => {
    if (!tripRef.current || isSharing) return;
    if (isDemo) {
      toast({
        title: "Demo trips can't be shared",
        description: "Create your own trip first to get a shareable link.",
      });
      return;
    }
    setIsSharing(true);
    try {
      const resp = await fetch("/api/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trip: tripRef.current }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const reason =
          resp.status === 503
            ? "Sharing isn't configured yet. Try again once it's set up."
            : data?.error || "Couldn't create a share link. Try again in a moment.";
        toast({ title: "Share failed", description: reason, variant: "destructive" });
        return;
      }
      trackTripShared();
      const shareUrl = `${window.location.origin}${data.url}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Share link copied",
          // Says what stays private, not only what is exposed. The share
          // payload strips chat, notes and booking references (api/share.js).
          description:
            "Anyone with the link can view it read-only. Your notes, chat and booking references stay private.",
        });
      } catch {
        // Clipboard blocked; surface the URL so the user can copy manually.
        toast({
          title: "Share link ready",
          description: shareUrl,
        });
      }
    } catch (err) {
      toast({
        title: "Share failed",
        description:
          err instanceof Error
            ? err.message
            : "Couldn't reach the server. Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    setTrip((prev) => {
      const dayIndex = prev.days.findIndex((d) =>
        d.items.some((i) => i.id === activeId)
      );
      if (dayIndex === -1) return prev;

      const day = prev.days[dayIndex];
      const oldIdx = day.items.findIndex((i) => i.id === activeId);
      const newIdx = day.items.findIndex((i) => i.id === overId);

      if (oldIdx === -1 || newIdx === -1) return prev;

      const destBucket = day.items[newIdx].timeBucket;
      let newItems = arrayMove(day.items, oldIdx, newIdx);
      newItems = newItems.map((it, idx) =>
        idx === newIdx ? { ...it, timeBucket: destBucket } : it
      );

      const nextDays = [...prev.days];
      nextDays[dayIndex] = { ...day, items: newItems };
      return { ...prev, days: nextDays };
    });
  };

  const applyToolCall = (toolName: string, input: any) => {
    // Funnel: the AI produced real structure for the first time (first day
    // or item on a trip). Fire once per trip mount.
    if (
      !isDemo &&
      !tripBuiltTracked.current &&
      (toolName === "add_day" || toolName === "add_item")
    ) {
      tripBuiltTracked.current = true;
      trackTripBuilt();
    }
    if (toolName === "update_trip_metadata") {
      const changes = (input.changes ?? {}) as Partial<Trip>;
      setTrip((prev) => {
        // Whitelist the fields this tool may touch (matches the server
        // schema: title, destination, startDate, endDate). The schema does
        // not set additionalProperties:false, so an unfiltered spread would
        // let a stray changes.days or changes.id from the model wipe the
        // itinerary or orphan the trip from its journeys entry.
        const next: Trip = { ...prev };
        if (typeof changes.title === "string") next.title = changes.title;
        if (typeof changes.destination === "string")
          next.destination = changes.destination;
        if (typeof changes.startDate === "string")
          next.startDate = changes.startDate;
        if (typeof changes.endDate === "string")
          next.endDate = changes.endDate;
        if (
          changes.destination &&
          changes.destination !== prev.destination
        ) {
          next.coverImage = getCoverImage(changes.destination);
        }
        return next;
      });
      return;
    }

    if (toolName === "add_day") {
      const date: string = input.date;
      const title: string = input.title || "";
      if (!date) return;
      const dayId = newId("day");
      setTrip((prev) => {
        if (prev.days.some((d) => d.date === date)) return prev;
        const nextDays = sortDays([
          ...prev.days,
          { id: dayId, date, title, items: [] },
        ]);
        return { ...prev, days: nextDays };
      });
      return;
    }

    if (toolName === "remove_day") {
      const dayIdInput: string | undefined = input.dayId;
      const dateInput: string | undefined = input.date;
      setTrip((prev) => {
        const day = findDay(prev.days, dayIdInput, dateInput);
        if (!day) return prev;
        return {
          ...prev,
          days: prev.days.filter((d) => d.id !== day.id),
        };
      });
      return;
    }

    if (toolName === "add_day_note") {
      const note = typeof input.note === "string" ? input.note.trim() : "";
      if (!note) return;
      const dayIdInput: string | undefined = input.dayId;
      const dateInput: string | undefined = input.date;
      setTrip((prev) => {
        const day = findDay(prev.days, dayIdInput, dateInput);
        if (!day) {
          console.warn(
            `add_day_note: no day found for dayId=${dayIdInput} date=${dateInput}`
          );
          return prev;
        }
        if ((day.notes ?? []).includes(note)) return prev;
        return {
          ...prev,
          days: prev.days.map((d) =>
            d.id === day.id ? { ...d, notes: [...(d.notes ?? []), note] } : d
          ),
        };
      });
      return;
    }

    if (toolName === "add_item") {
      const dayIdInput: string | undefined = input.dayId;
      const dateInput: string | undefined = input.date;
      const itemInput = input.item ?? {};
      // If the stream was cut off mid-call, the tool input may be incomplete
      // and the title missing/empty. Skip silently rather than seeding the
      // itinerary with an "Untitled" placeholder the user has to clean up.
      const rawTitle =
        typeof itemInput.title === "string" ? itemInput.title.trim() : "";
      if (!rawTitle) {
        console.warn("add_item: skipping item with missing/empty title", input);
        return;
      }
      const itemId = newId("item");
      const insertBeforeItemId: string | undefined = input.insertBeforeItemId;
      const insertAfterItemId: string | undefined = input.insertAfterItemId;

      setTrip((prev) => {
        const day = findDay(prev.days, dayIdInput, dateInput);
        if (!day) {
          console.warn(
            `add_item: no day found for dayId=${dayIdInput} date=${dateInput}`
          );
          return prev;
        }
        const source =
          itemInput.source && typeof itemInput.source.label === "string"
            ? {
                label: String(itemInput.source.label),
                url:
                  typeof itemInput.source.url === "string"
                    ? itemInput.source.url
                    : undefined,
              }
            : undefined;
        const newItem: ItineraryItem = {
          id: itemId,
          coordinates: { lat: 0, lng: 0 },
          timeBucket: normalizeBucket(itemInput.timeBucket),
          title: rawTitle,
          description: itemInput.description ?? "",
          type: normalizeItemType(itemInput.type),
          specificTime: itemInput.specificTime,
          address: itemInput.address,
          hours: itemInput.hours,
          priceRange: itemInput.priceRange,
          bookingUrl: itemInput.bookingUrl,
          notes: itemInput.notes,
          duration: itemInput.duration,
          googlePlaceId: itemInput.googlePlaceId,
          source,
          checkIn:
            typeof itemInput.checkIn === "string"
              ? itemInput.checkIn
              : undefined,
          checkOut:
            typeof itemInput.checkOut === "string"
              ? itemInput.checkOut
              : undefined,
          bookingReference:
            typeof itemInput.bookingReference === "string"
              ? itemInput.bookingReference.trim() || undefined
              : undefined,
        };
        return {
          ...prev,
          days: prev.days.map((d) =>
            d.id === day.id
              ? {
                  ...d,
                  items: insertItem(
                    d.items,
                    newItem,
                    insertBeforeItemId,
                    insertAfterItemId
                  ),
                }
              : d
          ),
        };
      });

      const destination = tripRef.current?.destination ?? "";
      const geocodeQuery =
        itemInput.address ||
        `${itemInput.title}${destination ? `, ${destination}` : ""}`;
      geocodeAddress(geocodeQuery).then((coords) => {
        if (!coords) return;
        setTrip((prev) => ({
          ...prev,
          days: prev.days.map((d) => ({
            ...d,
            items: d.items.map((it) =>
              it.id === itemId ? { ...it, coordinates: coords } : it
            ),
          })),
        }));
      });
      return;
    }

    if (toolName === "update_item") {
      const itemId: string = input.itemId;
      const rawChanges = (input.changes ?? {}) as Record<string, any>;
      const changes: Partial<ItineraryItem> = { ...rawChanges };
      if (rawChanges.type !== undefined) {
        changes.type = normalizeItemType(rawChanges.type);
      }
      if (rawChanges.timeBucket !== undefined) {
        changes.timeBucket = normalizeBucket(rawChanges.timeBucket);
      }

      setTrip((prev) => ({
        ...prev,
        days: prev.days.map((d) => ({
          ...d,
          items: d.items.map((it) =>
            it.id === itemId ? { ...it, ...changes } : it
          ),
        })),
      }));

      if (changes.address || changes.title) {
        const destination = tripRef.current?.destination ?? "";
        const updated = tripRef.current?.days
          .flatMap((d) => d.items)
          .find((it) => it.id === itemId);
        const query =
          changes.address ||
          (changes.title
            ? `${changes.title}${destination ? `, ${destination}` : ""}`
            : updated?.address ||
              (updated
                ? `${updated.title}${destination ? `, ${destination}` : ""}`
                : ""));
        if (query) {
          geocodeAddress(query).then((coords) => {
            if (!coords) return;
            setTrip((prev) => ({
              ...prev,
              days: prev.days.map((d) => ({
                ...d,
                items: d.items.map((it) =>
                  it.id === itemId ? { ...it, coordinates: coords } : it
                ),
              })),
            }));
          });
        }
      }
      return;
    }

    if (toolName === "remove_item") {
      const itemId: string = input.itemId;
      setTrip((prev) => ({
        ...prev,
        days: prev.days.map((d) => ({
          ...d,
          items: d.items.filter((i) => i.id !== itemId),
        })),
      }));
      return;
    }

    if (toolName === "set_essentials") {
      const raw = Array.isArray(input.essentials) ? input.essentials : [];
      const cleaned = raw
        .map((e: any) => {
          if (
            !e ||
            typeof e.topic !== "string" ||
            typeof e.content !== "string"
          ) {
            return null;
          }
          return {
            topic: e.topic.trim(),
            content: e.content.trim(),
            url: typeof e.url === "string" ? e.url : undefined,
          };
        })
        .filter(Boolean) as { topic: string; content: string; url?: string }[];
      setTrip((prev) => ({ ...prev, essentials: cleaned }));
      return;
    }
  };

  const friendlyErrorCopy = (err: ConciergeError): string => {
    if (err.code === "daily_limit") {
      // err.message already names the limit ("You've reached today's limit
      // of 25 messages."); append the reset time when we have it.
      let when = "";
      if (err.resetAt) {
        const reset = new Date(err.resetAt);
        if (!Number.isNaN(reset.getTime())) {
          const sameDay = reset.toDateString() === new Date().toDateString();
          const time = reset.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          });
          when = sameDay ? ` It resets at ${time}.` : " It resets tomorrow.";
        }
      }
      return `${err.message}${when} This keeps the concierge sustainable while SundayAtlas is free.`;
    }
    if (err.code === "rate_limit") {
      return "Our concierge is at capacity right now. Give it a minute and try again.";
    }
    if (err.code === "network") {
      return "Couldn't reach the concierge. Check your connection and try again.";
    }
    // Something about the conversation itself is being refused, so telling
    // the reader to try again in a moment would be false advice: the same
    // send will fail the same way.
    if (err.code === "bad_request") {
      return "The concierge couldn't read this conversation. Retrying won't change that, so start a fresh trip if it keeps happening.";
    }
    return "Something went wrong on our end. Try again in a moment.";
  };

  const streamReply = async (
    assistantMsgId: string,
    messagesForAPI: ConciergeMessage[]
  ) => {
    setIsStreaming(true);
    setStreamingMsgId(assistantMsgId);
    setFailedMsgId(null);
    setStreamStatus("Thinking...");

    const { localDate, tzOffsetMinutes } = localDayParams();
    // Consume the preset id on the first send only; later turns must not
    // re-inject (and re-bill) the starter-picks block.
    const presetId = pendingPresetId.current;
    pendingPresetId.current = null;
    await streamConcierge(
      {
        messages: messagesForAPI,
        trip: tripRef.current,
        preferences,
        localDate,
        tzOffsetMinutes,
        accessToken,
        presetId: presetId ?? undefined,
      },
      {
        onText: (delta) => {
          // Once real text arrives, the status line is no longer needed.
          setStreamStatus(null);
          setTrip((prev) => ({
            ...prev,
            chatHistory: prev.chatHistory.map((m) =>
              m.id === assistantMsgId
                ? { ...m, content: m.content + delta }
                : m
            ),
          }));
        },
        onToolCall: (name, input) => {
          // Surface what's happening. Only client tools reach here; the
          // structural ones map to "Building your trip".
          setStreamStatus(toolStatusLabel(name));
          if (name === "present_options") {
            // Chat-UI tool, not an itinerary tool: attach the cards to the
            // assistant message that is streaming right now.
            const normalized = normalizeOptionsInput(input);
            if (!normalized) {
              console.warn("present_options: unusable input, ignoring", input);
              return;
            }
            trackDecisionCardsShown();
            // First call wins: a second present_options in the same reply
            // (prompt-rule drift) must not silently destroy a pending fork.
            setTrip((prev) => ({
              ...prev,
              chatHistory: prev.chatHistory.map((m) =>
                m.id === assistantMsgId && !m.options
                  ? { ...m, options: normalized }
                  : m
              ),
            }));
            return;
          }
          applyToolCall(name, input);
        },
        onError: (err) => {
          // The underlying reason never reaches the screen, by design, so
          // put it somewhere a person can read when a report comes in.
          console.error("Concierge stream failed:", err.code, err.message);
          // If the stream already produced text before erroring, keep the
          // partial reply. Otherwise replace the empty bubble with friendly
          // copy. Either way, expose the Retry affordance.
          //
          // Trimmed, because "produced text" was true of a reply holding
          // only the paragraph break injected between agent turns. That
          // left a whitespace-only message in the history, which the API
          // rejects, so every later send in the trip failed for good.
          const copy = friendlyErrorCopy(err);
          setTrip((prev) => ({
            ...prev,
            chatHistory: prev.chatHistory.map((m) =>
              m.id === assistantMsgId
                ? { ...m, content: m.content.trim() ? m.content : copy }
                : m
            ),
          }));
          setIsStreaming(false);
          setStreamingMsgId(null);
          setStreamStatus(null);
          setFailedMsgId(assistantMsgId);
          // On a daily-budget block, snap the meter to 0 (with the reset
          // time) immediately so the user sees why; no Retry will help until
          // reset.
          if (err.code === "daily_limit") {
            usage.setFromLimit(err.resetAt ?? null);
          }
        },
        onDone: () => {
          setIsStreaming(false);
          setStreamingMsgId(null);
          setStreamStatus(null);
          // Refresh the ambient meter after each message lands.
          usage.refetch();
        },
      }
    );
  };

  const handleSend = async (overrideContent?: string) => {
    const content = (overrideContent ?? draft).trim();
    if (!content || isStreaming || atMessageLimit || !tripRef.current) return;

    // Funnel: this user message. Count prior user messages to detect the
    // first one (activation) and milestone depths. Non-identifying: counts
    // only, no message content.
    if (!isDemo) {
      const priorUserCount = tripRef.current.chatHistory.filter(
        (m) => m.role === "user"
      ).length;
      if (priorUserCount === 0) trackFirstMessageSent();
      const newCount = priorUserCount + 1;
      if (newCount === 5 || newCount === 10 || newCount === 20) {
        trackMessageMilestone(newCount);
      }
    }

    const userMsg: Message = {
      id: `msg-${Date.now()}`,
      role: "user",
      content,
      timestamp: Date.now(),
    };
    const assistantMsgId = `msg-${Date.now() + 1}`;
    const assistantMsg: Message = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      timestamp: Date.now() + 1,
    };

    const messagesForAPI: ConciergeMessage[] = [
      ...tripRef.current.chatHistory,
      userMsg,
    ].map(toApiMessage);

    setTrip((prev) => ({
      ...prev,
      chatHistory: [...prev.chatHistory, userMsg, assistantMsg],
    }));
    setDraft("");

    await streamReply(assistantMsgId, messagesForAPI);
  };

  // A decision-card tap: record the choice on the presenting message (so
  // the cards render inert with the pick highlighted, surviving reload)
  // and send it as the user's answer. Mirrors handleSend's guards
  // (streaming, trip loaded) BEFORE marking chosen, so a dropped send
  // can't leave a checkmarked card with no message behind it.
  const handleChooseOption = (messageId: string, label: string) => {
    if (isStreaming || !tripRef.current) return;
    trackDecisionCardChosen();
    setTrip((prev) => ({
      ...prev,
      chatHistory: prev.chatHistory.map((m) =>
        m.id === messageId && m.options
          ? { ...m, options: { ...m.options, chosenLabel: label } }
          : m
      ),
    }));
    handleSend(`Let's go with ${label}.`);
  };

  const handleRetry = async (failedAssistantId: string) => {
    if (isStreaming || !tripRef.current) return;

    // Clear the failed assistant message so it streams in place. options
    // too: cards attached by the failed attempt must not survive under a
    // retried reply that may never re-present that fork.
    setTrip((prev) => ({
      ...prev,
      chatHistory: prev.chatHistory.map((m) =>
        m.id === failedAssistantId
          ? { ...m, content: "", options: undefined }
          : m
      ),
    }));

    // Send everything in history except the empty assistant placeholder we
    // just cleared. The original user message remains and gets retried.
    const messagesForAPI: ConciergeMessage[] = tripRef.current.chatHistory
      .filter((m) => m.id !== failedAssistantId)
      .map(toApiMessage);

    await streamReply(failedAssistantId, messagesForAPI);
  };

  // Auto-send a prompt passed via navigate state from the landing page.
  // Fires once after the trip loads, then clears history state so refresh
  // doesn't re-trigger.
  useEffect(() => {
    if (initialPromptConsumed.current) return;
    if (!initialPromptFromState) return;
    if (!trip || isStreaming) return;
    initialPromptConsumed.current = true;
    window.history.replaceState({}, "");
    handleSend(initialPromptFromState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id, initialPromptFromState]);

  const headerSubtext = useMemo(() => {
    if (!trip) return "";
    const range = formatDateRange(trip.startDate, trip.endDate);
    const dayCount = trip.days.length;
    if (range && dayCount > 0) return `${range} · ${dayCount} days`;
    if (range) return range;
    if (dayCount > 0) return `${dayCount} days`;
    return "Dates not set yet";
  }, [trip?.startDate, trip?.endDate, trip?.days.length]);

  // All hooks have now run. Safe to bail out for the loading/empty state
  // without violating hook order (this is below every hook, above the
  // main return). If `trip` briefly goes null (e.g. the trips list empties
  // on a failed load) the component shows a loading state instead of
  // crashing with React #300.
  if (!trip) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-sm text-ink-muted">Loading trip...</p>
      </div>
    );
  }

  const isEmpty = trip.days.length === 0;
  // Which day (if any) is today, for the rail's "today" marker.
  const todayDayIndex = (() => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const i = trip?.days.findIndex((d) => d.date === iso) ?? -1;
    return i >= 0 ? i : null;
  })();

  // One-line preview of the last assistant turn, shown while the concierge
  // panel is collapsed.
  const desktopChatPreview = (() => {
    const last = [...(trip?.chatHistory ?? [])].reverse().find((m) => m.role === "assistant");
    const text = last?.content?.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
    return text || "Ask anything about the trip...";
  })();

  // One source of truth for the composer's placeholder. The limit wins over
  // everything, as it does in the artboard; streaming copy used to live
  // inside ChatInput, which could not know about the limit.
  const inputPlaceholder = atMessageLimit
    ? "Back tomorrow"
    : isStreaming
    ? "Concierge is responding..."
    : isEmpty
    ? "Where to, and when?"
    : "Ask anything about the trip...";
  // The composer is inert at the limit: dimmed pill, no typing, no send.
  const composerDisabled = isStreaming || atMessageLimit;

  // Editorial hero shown atop the itinerary. Only render once the trip
  // has a destination (a brand-new empty journey has none yet, and a
  // generic placeholder hero would look broken).
  const itineraryHero = trip.destination?.trim() ? (
    <DestinationHero
      title={trip.title}
      destination={trip.destination}
      coverImage={trip.coverImage}
      dateRange={formatDateRange(trip.startDate, trip.endDate)}
      dayCount={trip.days.length}
      stopCount={totalStops(trip.days)}
    />
  ) : null;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="h-screen flex flex-col bg-background">
        {/* Header cluster: back, masthead, then the date kicker, Share, theme,
            account and the trip menu. The trip's own title is not here; it is
            the chapter title on the canvas. */}
        {/* Below md, per #m-trip: 20/16 padding, the chevron pulled 14px
            into the gutter, an 11px/.28em masthead, and four adjacent 44px
            controls pulled 12px into the right gutter. */}
        <header className="border-b border-ink h-14 pl-5 pr-4 md:pl-8 md:pr-[22px] flex items-center justify-between shrink-0 relative z-[6]">
          <div className="flex items-center gap-2 md:gap-[18px] min-w-0">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center h-11 w-11 -ml-3.5 md:-ml-3 text-ink shrink-0"
              aria-label="Back to my trips"
            >
              <ChevronLeft className="h-[18px] w-[18px]" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center min-h-[44px] font-sans text-[11px] tracking-[0.28em] md:text-xs md:tracking-[0.32em] font-bold uppercase text-ink whitespace-nowrap"
            >
              Sunday Atlas
            </Link>
          </div>

          <div className="flex items-center gap-0 md:gap-1 -mr-3 md:mr-0 shrink-0 font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-muted">
            {headerSubtext && (
              <span className="hidden md:flex items-center gap-1.5 mr-3.5">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent" />
                {headerSubtext}
              </span>
            )}
            <button
              onClick={() => handleShare()}
              disabled={isSharing}
              className="inline-flex items-center justify-center gap-[7px] min-h-[44px] min-w-[44px] px-2.5 text-ink hover:text-accent-text transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{isSharing ? "Creating link..." : "Share"}</span>
            </button>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
              className="h-11 w-11 md:h-9 md:w-9 rounded-full text-ink-muted hover:text-ink hover:bg-paper-hover flex items-center justify-center transition-colors"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </button>
            <AccountMenu variant="compact" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label="Open trip menu"
                  className="h-11 w-11 md:h-9 md:w-9 rounded-full text-ink-muted hover:text-ink hover:bg-paper-hover flex items-center justify-center transition-colors"
                >
                  <MenuIcon className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer">
                    <HomeIcon className="h-4 w-4 mr-2" />
                    Home
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/journey/new" className="cursor-pointer">
                    <Plus className="h-4 w-4 mr-2" />
                    New trip
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => handleShare()}
                  disabled={isSharing}
                  className="cursor-pointer"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {isSharing ? "Creating link..." : "Share trip"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-ink-muted font-medium">
                  Your trips
                </DropdownMenuLabel>
                {(() => {
                  const others = journeys.filter((j) => j.id !== trip.id);
                  if (others.length === 0) {
                    return (
                      <div className="px-2 py-1.5 text-xs text-ink-muted">
                        No other trips yet.
                      </div>
                    );
                  }
                  return others.slice(0, 8).map((j) => {
                    const label =
                      j.title?.trim() || j.destination?.trim() || "Untitled trip";
                    return (
                      <DropdownMenuItem key={j.id} asChild>
                        <Link
                          to={`/journey/${j.id}`}
                          className="cursor-pointer truncate"
                        >
                          {label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  });
                })()}
                {journeys.filter((j) => j.id !== trip.id).length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/dashboard"
                        className="cursor-pointer text-xs text-ink-muted"
                      >
                        View all trips
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* ===== Desktop layout (>= lg): contents rail | chapter | sidebar.
            The concierge is docked at the sidebar's foot rather than being a
            floating panel, per "Trip - Field Notes.dc.html". ===== */}
        <div className="hidden lg:flex flex-1 min-h-0">
          <ContentsRail
            days={trip.days}
            activePage={activePage}
            onSelect={setActivePage}
            essentialsCount={trip.essentials?.length ?? 0}
            open={tocOpen}
            onToggleOpen={() => setTocOpen((o) => !o)}
            todayIndex={todayDayIndex}
          />

          <main className="flex-1 min-w-0 overflow-y-auto">
            {activePage === "essentials" ? (
              <div className="px-6 md:px-14 pt-8 pb-10 md:pt-12 md:pb-16">
                <div className="font-sans text-[11px] font-bold tracking-[0.22em] uppercase text-accent-text mb-2.5">
                  Things to know · {trip.essentials?.length ?? 0}{" "}
                  {(trip.essentials?.length ?? 0) === 1 ? "essential" : "essentials"} for this trip
                </div>
                <h1 className="relative inline-block font-serif tracking-normal text-[32px] leading-[1.12] md:text-[46px] md:leading-[1.1] mb-2">
                  Before you go
                  <svg viewBox="0 0 300 14" fill="none" aria-hidden="true" className="absolute left-0 -bottom-2 md:-bottom-2.5 w-full h-3 md:h-3.5">
                    <path d="M4 9 C60 3, 150 11, 296 5" stroke="var(--fn-accent)" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </h1>
                {/* The artboard shows the intro only once there is something to
                    introduce; the empty state speaks for itself. */}
                {(trip.essentials?.length ?? 0) > 0 && (
                  <p className="font-serif text-[17px] leading-[1.6] text-ink-muted max-w-[560px] mt-[22px] mb-9">
                    Practical notes the concierge gathered{trip.destination ? ` for ${trip.destination}` : ""}. It adds to this page as the trip takes shape.
                  </p>
                )}
                {(trip.essentials?.length ?? 0) > 0 ? (
                  <div className="border-t border-ink max-w-[720px]">
                    {trip.essentials!.map((e, i) => (
                      <div key={i} className="grid grid-cols-[150px_1fr_28px] items-baseline gap-5 py-[18px] border-b border-rule-soft">
                        <span className="font-sans text-[10.5px] font-bold tracking-[0.18em] uppercase text-ink">{e.topic}</span>
                        <span className="font-serif text-base leading-[1.6] text-ink-soft">{e.content}</span>
                        {e.url ? (
                          <a
                            href={e.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${e.topic} link`}
                            className="text-ink-faint hover:text-accent-text inline-flex justify-self-end p-1"
                          >
                            <ExternalLink className="h-[13px] w-[13px]" />
                          </a>
                        ) : (
                          <span />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-[30px] border border-dashed border-rule-dash-2 rounded-[4px] px-[30px] py-7 max-w-[640px]">
                    <p className="font-serif italic text-base leading-[1.6] text-ink-faint">
                      Nothing yet. As the concierge builds your trip, it'll add visa, plug type,
                      SIM, tipping, transit, and other practical notes here.
                    </p>
                  </div>
                )}
              </div>
            ) : trip.days.length === 0 ? (
              <div className="px-6 md:px-12 py-10">
                <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mb-3">New trip</h1>
                <p className="font-serif italic text-[17px] text-ink-faint max-w-[560px]">
                  Tell the concierge where you're headed and roughly when. It drafts the days here.
                </p>
              </div>
            ) : (
              <DayChapter
                day={trip.days[Math.min(Number(activePage), trip.days.length) - 1]}
                index={Math.min(Number(activePage), trip.days.length) - 1}
                total={trip.days.length}
                onSelectItem={handleSelectItem}
                onEdit={handleEditItem}
                onAsk={handleAskItem}
                onRemove={handleRemoveItem}
                onSaveItemNote={handleSaveItemNote}
                onSaveDayNotes={handleSaveDayNotes}
                onAskOpenSlot={handleAskOpenSlot}
                onInvalidPlace={handleInvalidPlace}
                onDragEnd={handleDragEnd}
                tripId={trip.id}
                destination={trip.destination}
                getBookingHref={getBookingHref}
                onPrev={() => setActivePage((p) => Math.max(1, Number(p) - 1))}
                onNext={() => setActivePage((p) => Math.min(trip.days.length, Number(p) + 1))}
                building={isStreaming}
              />
            )}
          </main>

          {/* Sidebar: Map | Inspiration, with the concierge docked at the foot. */}
          <aside className="w-[350px] border-l border-rule shrink-0 flex flex-col min-h-0">
            <div className="flex gap-[22px] px-5 border-b border-rule shrink-0 items-stretch">
              {(["map", "inspiration"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setDesktopRightTab(tab)}
                  className={`kicker py-3.5 border-b-2 -mb-px transition-colors ${
                    desktopRightTab === tab
                      ? "border-accent text-ink"
                      : "border-transparent text-ink-faint hover:text-ink"
                  }`}
                >
                  {tab === "map" ? "Map" : "Inspiration"}
                </button>
              ))}
              <span className="flex-1" />
              <span className="self-center font-serif italic text-[12.5px] text-ink-faint truncate">
                {desktopRightTab === "map"
                  ? activePage === "essentials"
                    ? trip.destination
                    : `Day ${Number(activePage)}`
                  : inspirationCounts.desktop === null || !trip.destination
                  ? trip.destination
                  : `${inspirationCounts.desktop} videos · ${trip.destination}`}
              </span>
            </div>

            <div className="flex-1 relative min-h-0 overflow-hidden">
              {desktopRightTab === "map" ? (
                <TripMap
                  days={trip.days}
                  destination={trip.destination}
                  destinationCenter={destinationCenter}
                  expandedItemId={expandedItemId}
                  onPinClick={handlePinClick}
                  onClearSelection={handleClearSelection}
                />
              ) : (
                <div className="absolute inset-0 overflow-y-auto pb-[150px]">
                  <Inspiration
                    destination={trip.destination}
                    onAddPlace={handleInspirationAdd}
                    days={inspirationDayOptions}
                    activeDayNumber={
                      activePage === "essentials" ? 1 : Number(activePage)
                    }
                    onVideoCountChange={reportDesktopVideoCount}
                    tripId={trip.id}
                    deferPrefetch={isStreaming}
                  />
                </div>
              )}

              {/* Docked concierge. Collapsed it is a 146px strip; expanded it
                  grows to the sidebar height minus an 84px map strip, so the
                  map stays reachable and the tabs stay visible. */}
              <div
                className="absolute inset-x-0 bottom-0 bg-paper border-t border-rule flex flex-col z-[4] transition-[height] duration-[240ms] ease-out"
                style={{ height: isDesktopChatOpen ? "calc(100% - 84px)" : "146px" }}
              >
                <div className="flex items-center gap-2.5 pl-5 pr-3.5 pt-3 pb-1.5 shrink-0">
                  <button
                    onClick={() => setIsDesktopChatOpen((o) => !o)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left py-1"
                  >
                    <span className="font-sans text-[11px] font-bold tracking-[0.2em] uppercase leading-[1.2] text-ink-faint">
                      Concierge
                    </span>
                    <UsageMeter remaining={usage.remaining} limit={usage.limit} resetAt={usage.resetAt} />
                  </button>
                  <button
                    onClick={() => setIsDesktopChatOpen((o) => !o)}
                    aria-label={isDesktopChatOpen ? "Collapse conversation" : "Open conversation"}
                    className="h-8 w-8 rounded-full border border-rule bg-white inline-flex items-center justify-center shrink-0 text-ink-muted hover:border-ink hover:text-ink transition-colors"
                  >
                    {isDesktopChatOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {isDesktopChatOpen ? (
                  <div className="flex-1 overflow-y-auto px-5 pb-3 min-h-0">
                    {trip.chatHistory.map((msg) => (
                      <ChatMessage
                        key={msg.id}
                        message={msg}
                        isStreaming={msg.id === streamingMsgId}
                        statusLabel={msg.id === streamingMsgId ? streamStatus ?? undefined : undefined}
                        showRetry={msg.id === failedMsgId && !isStreaming}
                        onRetry={() => handleRetry(msg.id)}
                        onChooseOption={handleChooseOption}
                        chatBusy={isStreaming}
                        optionsExpired={msg.id !== trip.chatHistory[trip.chatHistory.length - 1]?.id}
                      />
                    ))}
                    <div ref={desktopChatBottomRef} />
                  </div>
                ) : (
                  <button
                    onClick={() => setIsDesktopChatOpen(true)}
                    className="block w-full shrink-0 text-left px-5 pb-2 font-serif italic text-[13.5px] leading-[1.4] text-ink-muted truncate"
                  >
                    {desktopChatPreview}
                  </button>
                )}

                <ChatInput
                  ref={desktopInputRef}
                  value={draft}
                  onChange={setDraft}
                  onSend={() => handleSend()}
                  disabled={composerDisabled}
                  placeholder={inputPlaceholder}
                  minRows={1}
                  variant="docked"
                />
              </div>
            </div>
          </aside>
        </div>


        {/* ===== Mobile layout (< lg): chapter chips + canvas + docked
            concierge bar, per Mobile - Field Notes.dc.html#m-trip. The old
            Itinerary / Map / Inspo tab bar is gone: chapters are chips, and
            the map and inspiration are entry points in the day. ===== */}
        <div className="lg:hidden flex-1 flex flex-col min-h-0">
          <div className="flex gap-2 overflow-x-auto px-4 py-2.5 border-b border-rule shrink-0">
            <button
              onClick={() => setActivePage("essentials")}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 min-h-[44px] font-sans text-[11px] font-semibold border ${
                activePage === "essentials"
                  ? "bg-ink text-paper border-ink"
                  : "bg-transparent text-ink-muted border-rule-dash-2"
              }`}
            >
              <Compass className="h-3.5 w-3.5" />
              Before you go
              {(trip.essentials?.length ?? 0) > 0 && (
                <span className="font-serif italic text-xs opacity-70">
                  {trip.essentials!.length}
                </span>
              )}
            </button>
            {trip.days.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setActivePage(i + 1)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 min-h-[44px] font-sans text-[11px] font-semibold border ${
                  activePage === i + 1
                    ? "bg-ink text-paper border-ink"
                    : "bg-transparent text-ink-muted border-rule-dash-2"
                }`}
              >
                <span className={`font-serif italic text-xs ${activePage === i + 1 ? "text-accent-on-dark" : "text-ink-faint"}`}>
                  {i + 1}
                </span>
                <span className="whitespace-nowrap">{d.title || `Day ${i + 1}`}</span>
                {todayDayIndex === i && <span className="kicker text-accent-text">today</span>}
              </button>
            ))}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pb-28">
            {/* Inspiration opens from the head of the canvas, not its foot.
                Below the day it sat under every stop and the map, so it was
                only ever found by scrolling to the end of the day. */}
            <button
              onClick={() => setMobileTab("inspiration")}
              className="mx-[22px] mt-3.5 flex items-center gap-3 border border-rule bg-card px-4 py-3.5 min-h-[44px] text-left w-[calc(100%-44px)]"
            >
              <Youtube className="h-[18px] w-[18px] text-accent shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block kicker text-ink">Inspiration</span>
                <span className="block font-serif italic text-[13px] text-ink-muted mt-0.5">
                  {inspirationCounts.mobile !== null &&
                    `${inspirationCounts.mobile} `}
                  creator videos for {trip.destination || "this trip"} ·
                  paste a YouTube link
                </span>
              </span>
              <ChevronRight className="h-4 w-4 text-ink-faint shrink-0" />
            </button>

            {activePage === "essentials" ? (
              <div className="px-[22px] py-8">
                <div className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-accent-text mb-1.5">
                  Things to know · {trip.essentials?.length ?? 0}{" "}
                  {(trip.essentials?.length ?? 0) === 1 ? "essential" : "essentials"} for this trip
                </div>
                <h1 className="relative inline-block font-serif tracking-normal text-[32px] leading-[1.12] mb-2">
                  Before you go
                  <svg viewBox="0 0 300 14" fill="none" aria-hidden="true" className="absolute left-0 -bottom-2 w-full h-3">
                    <path d="M4 9 C60 3, 150 11, 296 5" stroke="var(--fn-accent)" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </h1>
                {(trip.essentials?.length ?? 0) > 0 && (
                  <p className="font-serif text-[14.5px] leading-[1.6] text-ink-muted mt-5">
                    Practical notes the concierge gathered{trip.destination ? ` for ${trip.destination}` : ""}. It adds to this page as the trip takes shape.
                  </p>
                )}
                {(trip.essentials?.length ?? 0) > 0 ? (
                  <div className="border-t border-ink mt-8">
                    {trip.essentials!.map((e, i) => (
                      <div key={i} className="flex items-baseline gap-3 py-3.5 border-b border-rule-soft">
                        <div className="min-w-0 flex-1">
                          <div className="font-sans text-[10.5px] font-bold tracking-[0.18em] uppercase text-ink mb-1.5">{e.topic}</div>
                          <div className="font-serif text-[14.5px] leading-[1.6] text-ink-soft">{e.content}</div>
                        </div>
                        {e.url && (
                          <a
                            href={e.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${e.topic} link`}
                            className="shrink-0 inline-flex items-center justify-center h-11 w-11 -my-3 -mr-3.5 text-ink-faint"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* No mobile artboard for this state; built from the desktop
                     equivalent and flagged for review in the running app. */
                  <div className="border border-dashed border-rule-dash-2 p-5 mt-8">
                    <p className="font-serif italic text-[15px] leading-[1.6] text-ink-muted">
                      Nothing yet. As the concierge builds your trip, it'll add visa, plug type,
                      SIM, tipping, transit, and other practical notes here.
                    </p>
                  </div>
                )}
              </div>
            ) : trip.days.length === 0 ? (
              <div className="px-[22px] py-8">
                <h1 className="font-serif text-[30px] leading-[1.1] mb-3">New trip</h1>
                <p className="font-serif italic text-[15px] text-ink-faint">
                  Tell the concierge where you're headed and roughly when. It drafts the days here.
                </p>
              </div>
            ) : (
              <DayChapter
                day={trip.days[Math.min(Number(activePage), trip.days.length) - 1]}
                index={Math.min(Number(activePage), trip.days.length) - 1}
                total={trip.days.length}
                onSelectItem={handleSelectItem}
                onEdit={handleEditItem}
                onAsk={handleAskItem}
                onRemove={handleRemoveItem}
                onSaveItemNote={handleSaveItemNote}
                onSaveDayNotes={handleSaveDayNotes}
                onAskOpenSlot={handleAskOpenSlot}
                onInvalidPlace={handleInvalidPlace}
                onDragEnd={handleDragEnd}
                tripId={trip.id}
                destination={trip.destination}
                getBookingHref={getBookingHref}
                onPrev={() => setActivePage((p) => Math.max(1, Number(p) - 1))}
                onNext={() => setActivePage((p) => Math.min(trip.days.length, Number(p) + 1))}
                building={isStreaming}
              />
            )}

            {trip.days.some((d) => d.items.length > 0) && (
              <div className="mx-[22px] mb-[18px] border border-rule">
                <div className="h-[220px] relative">
                  <TripMap
                    days={trip.days}
                    destination={trip.destination}
                    destinationCenter={destinationCenter}
                    expandedItemId={expandedItemId}
                    onPinClick={handlePinClick}
                    onClearSelection={handleClearSelection}
                  />
                </div>
              </div>
            )}

          </div>

          {/* Inspiration is a full-height sheet on mobile, not a tab. */}
          {mobileTab === "inspiration" && (
            <div className="fixed inset-0 z-50 bg-paper flex flex-col">
              <div className="flex items-center gap-2.5 py-3 pl-[22px] pr-3 border-b border-ink shrink-0">
                <Youtube className="h-4 w-4 text-accent shrink-0" />
                <span className="font-sans text-[11px] font-bold tracking-[0.2em] uppercase text-ink shrink-0">
                  Inspiration
                </span>
                {trip.destination && inspirationCounts.mobile !== null && (
                  <span className="flex-1 min-w-0 truncate font-serif italic text-[12.5px] text-ink-faint">
                    {inspirationCounts.mobile} videos · {trip.destination}
                  </span>
                )}
                <button
                  onClick={() => setMobileTab("itinerary")}
                  aria-label="Close inspiration"
                  className="h-11 w-11 ml-auto inline-flex items-center justify-center text-ink"
                >
                  <X className="h-[18px] w-[18px]" />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <Inspiration
                  destination={trip.destination}
                  onAddPlace={handleInspirationAdd}
                  days={inspirationDayOptions}
                  activeDayNumber={
                    activePage === "essentials" ? 1 : Number(activePage)
                  }
                  onVideoCountChange={reportMobileVideoCount}
                  tripId={trip.id}
                  deferPrefetch={isStreaming}
                />
              </div>
            </div>
          )}
        </div>

        {/* ===== Mobile chat: floating button + bottom sheet =====
            Mirrors the desktop floating-bubble + slide-in model so the
            two platforms feel the same. The FAB sits bottom-right above
            the canvas; tapping opens a near-fullscreen sheet with the
            conversation + input. Auto-opens on empty trip, on streaming,
            and on Edit/Ask (via setIsChatExpanded). */}
        <div className="lg:hidden">
          {/* Docked bar, per #m-trip: the last assistant line as a preview
              plus a chevron that opens the sheet. Replaces the floating
              button, which covered the last stop on the day. */}
          {!isChatExpanded && (
            <div
              className="fixed inset-x-0 bottom-0 z-40 bg-paper border-t border-ink px-4 py-2 flex items-center gap-2"
              style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
            >
              <button
                onClick={() => setIsChatExpanded(true)}
                className="flex-1 min-w-0 text-left min-h-[44px] flex flex-col justify-center"
                aria-label="Open concierge chat"
              >
                <span className="block kicker text-ink-faint">Concierge</span>
                <span className="block font-serif italic text-[13px] leading-[1.4] text-ink-muted truncate">
                  {desktopChatPreview}
                </span>
              </button>
              <button
                onClick={() => setIsChatExpanded(true)}
                aria-label="Open concierge chat"
                className="h-11 w-11 shrink-0 inline-flex items-center justify-center"
              >
                <span className="h-9 w-9 rounded-full border border-rule bg-card text-ink-muted inline-flex items-center justify-center">
                  <ChevronUp className="h-4 w-4" />
                </span>
              </button>
            </div>
          )}

          {/* Backdrop + sheet. Sheet is its own scrim-backed surface so
              the conversation gets real focus (unlike the old buried
              bottom bar). */}
          {isChatExpanded && (
            <div
              className="fixed inset-0 z-40 bg-black/30"
              onClick={() => setIsChatExpanded(false)}
              aria-hidden="true"
            />
          )}
          <div
            // Height uses dvh (dynamic viewport) so the sheet shrinks when
            // the iOS keyboard opens, keeping the input + send button visible
            // above it. h-[90vh] is the fallback for browsers without dvh;
            // the [height:90dvh] arbitrary property overrides it where
            // supported (Tailwind emits both in source order).
            // No pb-safe-area here: the ChatInput at the bottom of this
            // sheet now owns the home-indicator inset (so its rounded
            // corners clear the edge). Adding it here too would double it.
            className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-rule rounded-t-2xl shadow-[0_-6px_24px_-4px_rgb(0_0_0_/_0.18)] flex flex-col transition-transform duration-200 ease-out h-[90vh] [height:90dvh]"
            style={{
              transform: isChatExpanded ? "translateY(0)" : "translateY(100%)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-rule shrink-0">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-accent-text" />
                <span className="text-sm font-medium">Concierge</span>
              </div>
              <div className="flex items-center gap-3">
                <UsageMeter
                  remaining={usage.remaining}
                  limit={usage.limit}
                  resetAt={usage.resetAt}
                />
                <button
                  onClick={() => setIsChatExpanded(false)}
                  className="h-11 w-11 -m-2 inline-flex items-center justify-center text-ink-muted hover:text-ink"
                  aria-label="Close chat"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4 min-h-0">
              {trip.chatHistory.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isStreaming={msg.id === streamingMsgId}
                  statusLabel={
                    msg.id === streamingMsgId ? streamStatus ?? undefined : undefined
                  }
                  showRetry={msg.id === failedMsgId && !isStreaming}
                  onRetry={() => handleRetry(msg.id)}
                  chatBusy={isStreaming}
                  optionsExpired={
                    msg.id !==
                    trip.chatHistory[trip.chatHistory.length - 1]?.id
                  }
                  onChooseOption={handleChooseOption}
                />
              ))}
              <div ref={chatBottomRef} />
            </div>
            <ChatInput
              ref={inputRef}
              value={draft}
              onChange={setDraft}
              onSend={() => handleSend()}
              disabled={composerDisabled}
              placeholder={inputPlaceholder}
              minRows={1}
            />
          </div>
        </div>
      </div>
    </APIProvider>
  );
};

export default Trip;
